/*
 * Marketplace specific configuration.
 *
 * Every filter needs to have following keys:
 * - id:     Unique id of the filter.
 * - label:  The default label of the filter.
 * - type:   String that represents one of the existing filter components:
 *           BookingDateRangeFilter, KeywordFilter, PriceFilter,
 *           SelectSingleFilter, SelectMultipleFilter.
 * - group:  Is this 'primary' or 'secondary' filter?
 *           Primary filters are visible on desktop layout by default.
 *           Secondary filters are behind "More filters" button.
 *           Read more from src/containers/SearchPage/README.md
 * - queryParamNames: Describes parameters to be used with queries
 *                    (e.g. 'price' or 'pub_amenities'). Most of these are
 *                    the same between webapp URLs and API query params.
 *                    You can't change 'dates', 'price', or 'keywords'
 *                    since those filters are fixed to a specific attribute.
 * - config: Extra configuration that the filter component needs.
 *
 * Note 1: Labels could be tied to translation file
 *         by importing FormattedMessage:
 *         <FormattedMessage id="some.translation.key.here" />
 *
 * Note 2: If you need to add new custom filter components,
 *         you need to take those into use in:
 *         src/containers/SearchPage/FilterComponent.js
 *
 * Note 3: If you just want to create more enum filters
 *         (i.e. SelectSingleFilter, SelectMultipleFilter),
 *         you can just add more configurations with those filter types
 *         and tie them with correct extended data key
 *         (i.e. pub_<key> or meta_<key>).
 */

const dev = process.env.REACT_APP_ENV === 'development';

export const languageSelectOptions = {
  en: { value: 'en', label: 'EN' },
  fr: { value: 'fr', label: 'FR' },
};

export const filters = [
  // A global time zone to use in availability searches. As listings
  // can be in various time zones, we must decide what time zone we
  // use in search when looking for available listings within a
  // certain time interval.
  //
  // If you have all/most listings in a certain time zone, change this
  // config value to that.
  //
  // See: https://en.wikipedia.org/wiki/List_of_tz_database_time_zones
  // searchTimeZone: 'Etc/UTC',
  {
    id: 'dates-length',
    label: 'Dates',
    type: 'BookingDateRangeLengthFilter',
    group: 'primary',
    // Note: BookingDateRangeFilter is fixed filter,
    // you can't change "queryParamNames: ['dates'],"
    queryParamNames: ['dates', 'minDuration'],
    config: {
      // A global time zone to use in availability searches. As listings
      // can be in various time zones, we must decide what time zone we
      // use in search when looking for available listings within a
      // certain time interval.
      //
      // If you have all/most listings in a certain time zone, change this
      // config value to that.
      //
      // See: https://en.wikipedia.org/wiki/List_of_tz_database_time_zones
      searchTimeZone: 'Etc/UTC',

      // Options for the minimum duration of the booking
      options: [
        { key: '0', label: 'Any length' },
        { key: '60', label: '1 hour', shortLabel: '1h' },
        { key: '120', label: '2 hours', shortLabel: '2h' },
      ],
    },
  },
  {
    id: 'price',
    label: 'Price',
    type: 'PriceFilter',
    group: 'primary',
    // Note: PriceFilter is fixed filter,
    // you can't change "queryParamNames: ['price'],"
    queryParamNames: ['price'],
    // Price filter configuration
    // Note: unlike most prices this is not handled in subunits
    config: {
      min: 0,
      max: 1000,
      step: 5,
    },
  },
  {
    id: 'keyword',
    label: 'Keyword',
    type: 'KeywordFilter',
    group: 'primary',
    // Note: KeywordFilter is fixed filter,
    // you can't change "queryParamNames: ['keywords'],"
    queryParamNames: ['keywords'],
    // NOTE: If you are ordering search results by distance
    // the keyword search can't be used at the same time.
    // You can turn on/off ordering by distance from config.js file.
    config: {},
  },
  {
    id: 'yogaStyles',
    label: 'Yoga styles',
    type: 'SelectMultipleFilter',
    group: 'secondary',
    queryParamNames: ['pub_yogaStyles'],
    config: {
      // Optional modes: 'has_all', 'has_any'
      // https://www.sharetribe.com/api-reference/marketplace.html#extended-data-filtering
      searchMode: 'has_all',

      // "key" is the option you see in Flex Console.
      // "label" is set here for this web app's UI only.
      // Note: label is not added through the translation files
      // to make filter customizations a bit easier.
      options: [
        { key: 'ashtanga', label: 'Ashtanga' },
        { key: 'hatha', label: 'Hatha' },
        { key: 'kundalini', label: 'Kundalini' },
        { key: 'restorative', label: 'Restorative' },
        { key: 'vinyasa', label: 'Vinyasa' },
        { key: 'yin', label: 'Yin' },
      ],
    },
  },
  {
    id: 'certificate',
    label: 'Certificate',
    type: 'SelectSingleFilter',
    group: 'secondary',
    queryParamNames: ['pub_certificate'],
    config: {
      // "key" is the option you see in Flex Console.
      // "label" is set here for the UI only.
      // Note: label is not added through the translation files
      // to make filter customizations a bit easier.
      options: [
        {
          key: 'none',
          label: 'None',
          hideFromFilters: true,
          hideFromListingInfo: true,
        },
        { key: '200h', label: 'Registered yoga teacher 200h' },
        { key: '500h', label: 'Registered yoga teacher 500h' },
      ],
    },
  },
  {
    id: 'venueType',
    label: 'Venue Type',
    type: 'SelectSingleFilter',
    group: 'primary',
    queryParamNames: ['pub_venueTypeName'],
    config: {
      options: [
        {
          key: 'airport',
          value: 'airport',
          label: {
            [languageSelectOptions.en.value]: 'Airport',
            [languageSelectOptions.fr.value]: 'Aéroport',
          },
        },
        {
          key: 'collegeUniversitySchool',
          value: 'collegeUniversitySchool',
          label: {
            [languageSelectOptions.en.value]: 'College / University / School',
            [languageSelectOptions.fr.value]: 'Collège/ Université / École',
          },
        },
        {
          key: 'commissarykitchen',
          value: 'commissarykitchen',
          label: {
            [languageSelectOptions.en.value]: 'Commissary Kitchen (food truck, cloud kitchen etc.)',
            [languageSelectOptions.fr.value]:
              'Cuisine sans local (camion de cuisine de rue, cuisine fantôme, etc.)',
          },
        },
        {
          key: 'conference',
          value: 'conference',
          label: {
            [languageSelectOptions.en.value]: 'Conference / Convention Center',
            [languageSelectOptions.fr.value]: 'Palais des congrès',
          },
        },
        {
          key: 'corporateBuilding',
          value: 'corporateBuilding',
          label: {
            [languageSelectOptions.en.value]: 'Corporate Building / Office',
            [languageSelectOptions.fr.value]: 'Bureau',
          },
        },
        {
          key: 'eventVenue',
          value: 'eventVenue',
          label: {
            [languageSelectOptions.en.value]: 'Event Venue / Banquet Hall',
            [languageSelectOptions.fr.value]: 'Salle de réception',
          },
        },
        {
          key: 'healthcare',
          value: 'healthcare',
          label: {
            [languageSelectOptions.en.value]: 'Healthcare Centre / Hospital',
            [languageSelectOptions.fr.value]: 'Clinique / Hôpital',
          },
        },
        {
          key: 'hotelResort',
          value: 'hotelResort',
          label: {
            [languageSelectOptions.en.value]: 'Hotel / Resort',
            [languageSelectOptions.fr.value]: 'Hôtel / Centre de villégiature',
          },
        },
        {
          key: 'lightIndustrial',
          value: 'lightIndustrial',
          label: {
            [languageSelectOptions.en.value]: 'Light Industrial',
            [languageSelectOptions.fr.value]: 'Industriel légère',
          },
        },
        {
          key: 'manufacturing',
          value: 'manufacturing',
          label: {
            [languageSelectOptions.en.value]: 'Manufacturing',
            [languageSelectOptions.fr.value]: 'Secteur industriel',
          },
        },
        {
          key: 'other',
          value: 'other',
          label: {
            [languageSelectOptions.en.value]: 'Other',
            [languageSelectOptions.fr.value]: 'Autre',
          },
        },
        {
          key: 'outdoorSpace',
          value: 'outdoorSpace',
          label: {
            [languageSelectOptions.en.value]: 'Outdoor Space / Offsite Venue',
            [languageSelectOptions.fr.value]: 'Espace extérieur / Lieu hors-site',
          },
        },
        {
          key: 'privateResidence',
          value: 'privateResidence',
          label: {
            [languageSelectOptions.en.value]: 'Private Residence',
            [languageSelectOptions.fr.value]: 'Résidence privé',
          },
        },
        {
          key: 'production',
          value: 'production',
          label: {
            [languageSelectOptions.en.value]: 'Production',
            [languageSelectOptions.fr.value]: 'Production',
          },
        },
        {
          key: 'restaurentBarCafe',
          value: 'restaurentBarCafe',
          label: {
            [languageSelectOptions.en.value]: 'Restaurant / Bar / Cafe',
            [languageSelectOptions.fr.value]: 'Restaurant / Bar / Café',
          },
        },
        {
          key: 'retailstore',
          value: 'retailstore',
          label: {
            [languageSelectOptions.en.value]: 'Retail Store / Shopping Center',
            [languageSelectOptions.fr.value]: 'Magasin de détail / Centre commercial',
          },
        },
        {
          key: 'seniorLiving',
          value: 'seniorLiving',
          label: {
            [languageSelectOptions.en.value]: 'Senior Living',
            [languageSelectOptions.fr.value]: 'Résidence pour personnes âgées',
          },
        },
        {
          key: 'socialCountrySportsClub',
          value: 'socialCountrySportsClub',
          label: {
            [languageSelectOptions.en.value]: 'Social Club / Country Club / Sports Club',
            [languageSelectOptions.fr.value]: 'Club de loisirs, d’activités sociales et sportives',
          },
        },
        {
          key: 'stadiumArena',
          value: 'stadiumArena',
          label: {
            [languageSelectOptions.en.value]: 'Stadium / Arena',
            [languageSelectOptions.fr.value]: 'Stade / Aréna',
          },
        },
        {
          key: 'touristsDestinations',
          value: 'touristsDestinations',
          label: {
            [languageSelectOptions.en.value]: 'Tourists Destinations (Museum, Zoo, Park, etc.)',
            [languageSelectOptions.fr.value]: 'Destinations touristiques (musée, zoo, parc, etc.)',
          },
        },
        {
          key: 'warehouse',
          value: 'warehouse',
          label: {
            [languageSelectOptions.en.value]: 'Warehouse',
            [languageSelectOptions.fr.value]: 'Entrepôt',
          },
        },
        {
          key: 'coldStorage',
          value: 'coldStorage',
          label: {
            [languageSelectOptions.en.value]: 'Warehouse - Cold Storage',
            [languageSelectOptions.fr.value]: 'Entrepôt - Chambre de réfrigération',
          },
        },
        {
          key: 'wineryBrewery',
          value: 'wineryBrewery',
          label: {
            [languageSelectOptions.en.value]: 'Winery / Brewery',
            [languageSelectOptions.fr.value]: 'Vignoble / Brasserie',
          },
        },
      ],
    },
  },
  {
    id: 'datePosted',
    label: 'Date Posted',
    type: 'SelectSingleFilter',
    group: 'primary',
    queryParamNames: ['pub_searchtime'],
    config: {
      options: [
        {
          hours: null,
          key: 'all',
          value: 'all',
          label: {
            [languageSelectOptions.en.value]: 'All',
            [languageSelectOptions.fr.value]: 'Toute',
          },
        },
        {
          hours: 24,

          key: 'last24hours',
          value: 'last24hours',
          label: {
            [languageSelectOptions.en.value]: 'Last 24 Hours',
            [languageSelectOptions.fr.value]: 'Dernières 24 heures',
          },
        },
        {
          hours: 72,

          key: 'lastThreeDays',
          value: 'lastThreeDays',
          label: {
            [languageSelectOptions.en.value]: 'Last 3 Days',
            [languageSelectOptions.fr.value]: '3 derniers jours',
          },
        },
        {
          hours: 168,

          key: 'lastOneWeek',
          value: 'lastOneWeek',
          label: {
            [languageSelectOptions.en.value]: 'Last 1 Week',
            [languageSelectOptions.fr.value]: '1 dernière semaine',
          },
        },
        {
          hours: 744,

          key: 'lastOneMonth',
          value: 'lastOneMonth',
          label: {
            [languageSelectOptions.en.value]: 'Last 1 Month',
            [languageSelectOptions.fr.value]: '1 dernier mois',
          },
        },
        {
          hours: 2160,

          key: 'lastThreeMonth',
          value: 'lastThreeMonth',
          label: {
            [languageSelectOptions.en.value]: 'Last 3 Months',
            [languageSelectOptions.fr.value]: '3 derniers mois',
          },
        },
        {
          key: 'lastSixMonth',
          value: 'lastSixMonth',
          label: {
            [languageSelectOptions.en.value]: 'Last 6 Months',
            [languageSelectOptions.fr.value]: '6 derniers mois',
          },
        },
        {
          hours: 8760,
          key: 'lastOneYear',
          value: 'lastOneYear',
          label: {
            [languageSelectOptions.en.value]: 'Last 1 Year',
            [languageSelectOptions.fr.value]: '1 dernier an',
          },
        },
      ],
    },
  },
  {
    id: 'category',
    label: 'Category',
    type: 'SelectSingleFilter',
    group: 'primary',
    queryParamNames: ['pub_Category'],
    config: {
      options: [
        {
          key: 'admin',
          value: 'admin',
          label: {
            [languageSelectOptions.en.value]: 'Administrative/Clerical',
            [languageSelectOptions.fr.value]: 'Administratif/travail de bureau',
          },
        },
        {
          key: 'hotel',
          value: 'hotel',
          label: {
            [languageSelectOptions.en.value]: 'Hotels, Restaurants & Events',
            [languageSelectOptions.fr.value]: 'Hôtellerie, Restauration et Événements',
          },
        },
        {
          key: 'logistic',
          value: 'logistic',
          label: {
            [languageSelectOptions.en.value]: 'Logistic & Production',
            [languageSelectOptions.fr.value]: 'Logistique et production',
          },
        },
        {
          key: 'retail',
          value: 'retail',
          label: {
            [languageSelectOptions.en.value]: 'Retail',
            [languageSelectOptions.fr.value]: 'Vente au détail',
          },
        },
        {
          key: 'other',
          value: 'other',
          label: {
            [languageSelectOptions.en.value]: 'Other',
            [languageSelectOptions.fr.value]: 'Autre',
          },
        },
      ],
    },
  },

  {
    id: 'adminSubcategory',
    label: 'Subcategory',
    type: 'SelectSingleFilter',
    group: 'secondary',
    queryParamNames: ['pub_subCategory'],
    config: {
      options: [
        {
          key: 'dataEntry',
          value: 'dataEntry',
          label: {
            [languageSelectOptions.en.value]: 'Data Entry',
            [languageSelectOptions.fr.value]: 'Saisie de données',
          },
        },
        {
          key: 'administrativeAssistance',
          value: 'administrativeAssistance',
          label: {
            [languageSelectOptions.en.value]: 'Administrative Assistant',
            [languageSelectOptions.fr.value]: 'Adjoint administratif',
          },
        },
        {
          key: 'receptionist',
          value: 'receptionist',
          label: {
            [languageSelectOptions.en.value]: 'Receptionist',
            [languageSelectOptions.fr.value]: 'Réceptionniste',
          },
        },
        {
          key: 'other',
          value: 'other',
          label: {
            [languageSelectOptions.en.value]: 'Other',
            [languageSelectOptions.fr.value]: 'Autre',
          },
        },
      ],
    },
  },

  {
    id: 'hotelSubcategory',
    label: 'Subcategory',
    type: 'SelectSingleFilter',
    group: 'secondary',
    queryParamNames: ['pub_subCategory'],
    config: {
      options: [
        {
          key: 'maintenanceAndCleaning',
          value: 'maintenanceAndCleaning',
          label: {
            en: 'Maintenance & Cleaning Staff',
            fr: 'Maintenance et entretien ménager',
          },
        },
        {
          key: 'valet',
          value: 'valet',
          label: {
            en: 'Valet',
            fr: 'Voiturier',
          },
        },
        {
          key: 'porter',
          value: 'porter',
          label: {
            en: 'Porter',
            fr: 'Bagagiste',
          },
        },

        {
          key: 'busser',
          value: 'busser',
          label: {
            en: 'Busser',
            fr: 'Commis débarrasseur',
          },
        },
        {
          key: 'concession',
          value: 'concession',
          label: {
            en: 'Concession/Stand Worker',
            fr: 'Commis aux kiosques et aux comptoirs de nourriture',
          },
        },
        {
          key: 'waiterWithTips',
          value: 'waiterWithTips',
          label: {
            en: 'Waiter (with tips)',
            fr: 'Serveur (avec pourboire)',
          },
        },
        {
          key: 'waiterWithOutTips',
          value: 'waiterWithOutTips',
          label: {
            en: 'Waiter (without tips)',
            fr: 'Serveur (sans pourboire)',
          },
        },
        {
          key: 'banquetWaiter',
          value: 'banquetWaiter',
          label: {
            en: 'Banquet Waiter',
            fr: 'Serveur de banquet',
          },
        },
        {
          key: 'buffetAttendant',
          value: 'buffetAttendant',
          label: {
            en: 'Buffet Attendant',
            fr: 'Préposé au buffet',
          },
        },
        {
          key: 'hostess',
          value: 'hostess',
          label: {
            en: 'Hostess',
            fr: 'Hôte',
          },
        },
        {
          key: 'bartenderWithTips',
          value: 'bartenderWithTips',
          label: {
            en: 'Bartender (with tips)',
            fr: 'Barman/Barmaid (avec pourboire)',
          },
        },
        {
          key: 'bartenderWithOutTips',
          value: 'bartenderWithOutTips',
          label: {
            en: 'Bartender (without tips)',
            fr: 'Barman/Barmaid (sans pourboire)',
          },
        },
        {
          key: 'dishwasher',
          value: 'dishwasher',
          label: {
            en: 'Dishwasher',
            fr: 'Plongeur',
          },
        },
        {
          key: 'barista',
          value: 'barista',
          label: {
            en: 'Barista',
            fr: 'Barista',
          },
        },
        {
          key: 'lineCooks',
          value: 'lineCooks',
          label: {
            en: 'Line Cook',
            fr: 'Cuisinier à la chaîne',
          },
        },
        {
          key: 'cook',
          value: 'cook',
          label: {
            en: 'Cook',
            fr: 'Cuisinier',
          },
        },
        {
          key: 'headCook',
          value: 'headCook',
          label: {
            en: 'Head Cook',
            fr: 'Chef cuisinier',
          },
        },

        {
          key: 'usher',
          value: 'usher',
          label: {
            en: 'Usher',
            fr: 'Placier',
          },
        },
        {
          key: 'securitypersonnel',
          value: 'securitypersonnel',
          label: {
            en: 'Security Personnel',
            fr: 'Personnel de sécurité',
          },
        },
        {
          key: 'ticketchecker',
          value: 'ticketchecker',
          label: {
            en: 'Ticket Checker/Scanner',
            fr: 'Vérificateur de billets',
          },
        },
        {
          key: 'eventStaff',
          value: 'eventStaff',
          label: {
            en: 'Event Staff',
            fr: 'Personnel événementiel',
          },
        },
        {
          key: 'other',
          value: 'other',
          label: {
            en: 'Other',
            fr: 'Autre',
          },
        },
      ],
    },
  },
  {
    id: 'logisticSubcategory',
    label: 'Subcategory',
    type: 'SelectSingleFilter',
    group: 'secondary',
    queryParamNames: ['pub_subCategory'],
    config: {
      options: [
        {
          key: 'orderPickerOrPacker',
          value: 'orderPickerOrPacker',
          label: {
            en: 'Order Picker/Packer',
            fr: 'Préparateur/emballeur de commandes',
          },
        },
        {
          key: 'productionOrAssemblyWorker',
          value: 'productionOrAssemblyWorker',
          label: {
            en: 'Production/Assembly Worker',
            fr: 'Ouvrier de production/assemblage',
          },
        },
        {
          key: 'qualityControlClerk',
          value: 'qualityControlClerk',
          label: {
            en: 'Quality Control Clerk',
            fr: 'Commis au contrôle de qualité',
          },
        },
        {
          key: 'wareHouseStaff',
          value: 'wareHouseStaff',
          label: {
            en: 'Warehouse Staff',
            fr: 'Commis d’entrepôt',
          },
        },
        {
          key: 'forkliftOperator',
          value: 'forkliftOperator',
          label: {
            en: 'Forklift Operator',
            fr: 'Conducteur de chariot élévateur',
          },
        },
        {
          key: 'inventoryTaker',
          value: 'inventoryTaker',
          label: {
            en: 'Inventory Taker',
            fr: 'Inventoriste',
          },
        },
        {
          key: 'deliveryDriver',
          value: 'deliveryDriver',
          label: {
            en: 'Delivery Driver',
            fr: 'Chauffeur-livreur',
          },
        },
        {
          key: 'deliveryNotDriver',
          value: 'deliveryNotDriver',
          label: {
            en: 'Delivery (not driver)',
            fr: 'Livreur (non chauffeur)',
          },
        },
        {
          key: 'foodPreparationOrProductionWorker',
          value: 'foodPreparationOrProductionWorker',
          label: {
            en: 'Food Preparation/Food Production Worker',
            fr: 'Commis à la préparation et production alimentaire',
          },
        },
        {
          key: 'generalLabourer',
          value: 'generalLabourer',
          label: {
            en: 'General Laborer',
            fr: 'Travailleur général',
          },
        },
        {
          key: 'other',
          value: 'other',
          label: {
            en: 'Other',
            fr: 'Autre',
          },
        },
      ],
    },
  },
  {
    id: 'retailSubcategory',
    label: 'Subcategory',
    type: 'SelectSingleFilter',
    group: 'secondary',
    queryParamNames: ['pub_subCategory'],
    config: {
      options: [
        {
          key: 'shelfStocker',
          value: 'shelfStocker',
          label: {
            en: 'Shelf Stocker',
            fr: 'Étalagiste',
          },
        },
        {
          key: 'groceryClerk',
          value: 'groceryClerk',
          label: {
            en: 'Grocery Clerk',
            fr: 'Commis d’épicerie',
          },
        },
        {
          key: 'groceryWrapper',
          value: 'groceryWrapper',
          label: {
            en: 'Grocery Wrapper',
            fr: 'Emballeur',
          },
        },
        {
          key: 'inventoryTaker',
          value: 'inventoryTaker',
          label: {
            en: 'Inventory Taker',
            fr: 'Inventoriste',
          },
        },
        {
          key: 'cashier',
          value: 'cashier',
          label: {
            en: 'Cashier',
            fr: 'Caissier',
          },
        },
        {
          key: 'generalLabourer',
          value: 'generalLabourer',
          label: {
            en: 'General Laborer',
            fr: 'Travailleur général',
          },
        },
        {
          key: 'other',
          value: 'other',
          label: {
            en: 'Other',
            fr: 'Autre',
          },
        },
      ],
    },
  },
  {
    id: 'otherSubcategory',
    label: 'Subcategory',
    type: 'SelectSingleFilter',
    group: 'secondary',
    queryParamNames: ['pub_subCategory'],
    config: {
      options: [
        {
          key: 'maintenanceAndCleaning',
          value: 'maintenanceAndCleaning',
          label: {
            en: 'Maintenance & Cleaning',
            fr: 'Maintenance et entretien',
          },
        },
        {
          key: 'securityPersonnel',
          value: 'securityPersonnel',
          label: {
            en: 'Security Personnel',
            fr: 'Personnel de sécurité',
          },
        },
        {
          key: 'landscapingAndGroundsMaintenance',
          value: 'landscapingAndGroundsMaintenance',
          label: {
            en: 'Landscaping and Grounds Maintenance',
            fr: 'Aménagement et entretien paysager',
          },
        },
        {
          key: 'snowRemoval',
          value: 'snowRemoval',
          label: {
            en: 'Snow Removal',
            fr: 'Déneigement',
          },
        },
        {
          key: 'agriculturalWorkers',
          value: 'agriculturalWorkers',
          label: {
            en: 'Agricultural Worker',
            fr: 'Travailleur agricole',
          },
        },
        {
          key: 'mover',
          value: 'mover',
          label: {
            en: 'Mover',
            fr: 'Déménageur',
          },
        },
        {
          key: 'brandAmbassador',
          value: 'brandAmbassador',
          label: {
            en: 'Brand Ambassador',
            fr: 'Ambassadeur de la marque',
          },
        },
        {
          key: 'hairDresserAssistant',
          value: 'hairDresserAssistant',
          label: {
            en: 'Hairdresser Assistant',
            fr: 'Assistant-coiffeur',
          },
        },
        {
          key: 'generalLabourer',
          value: 'generalLabourer',
          label: {
            en: 'General Laborer',
            fr: 'Travailleur général',
          },
        },
        {
          key: 'streetMarketing',
          value: 'streetMarketing',
          label: {
            en: 'Street Marketing',
            fr: 'Marketing de rue',
          },
        },
        {
          key: 'other',
          value: '1dollar',
          label: {
            en: '1dollar',
            fr: '1dollar',
          },
        },
      ],
    },
  },
  {
    id: 'minimumLevel',
    label: 'minimumLevel',
    type: 'SelectSingleFilter',
    group: 'secondary',
    queryParamNames: ['pub_minimumLevel'],
    config: {
      options: [
        {
          key: 'intermediate',
          value: 'intermediate',
          label: {
            [languageSelectOptions.en.value]: 'Intermediate',
            [languageSelectOptions.fr.value]: 'Intermédiaire',
          },
        },
        {
          key: 'medium',
          value: 'medium',
          label: {
            [languageSelectOptions.en.value]: 'Medium',
            [languageSelectOptions.fr.value]: 'Moyen',
          },
        },
        {
          key: 'expert',
          value: 'expert',
          label: {
            [languageSelectOptions.en.value]: 'Expert',
            [languageSelectOptions.fr.value]: 'Expert',
          },
        },
      ],
    },
  },
  {
    id: 'industry',
    label: 'Industry',
    type: 'SelectSingleFilter',
    group: 'primary',
    queryParamNames: ['pub_industry'],
    config: {
      options: [
        {
          key: 'AccommodationServices',
          value: 'AccommodationServices',
          label: {
            [languageSelectOptions.en.value]: 'Accommodation Services',
            [languageSelectOptions.fr.value]: `Services d'hébergement`,
          },
        },
        {
          key: 'AdministrativeSupportService',
          value: 'AdministrativeSupportService',
          label: {
            [languageSelectOptions.en.value]: 'Administrative and Support Service',
            [languageSelectOptions.fr.value]: 'Service administratif et de soutien',
          },
        },
        {
          key: 'Construction',
          value: 'Construction',
          label: {
            [languageSelectOptions.en.value]: 'Construction',
            [languageSelectOptions.fr.value]: 'Construction',
          },
        },
        {
          key: 'ConsumerServices',
          value: 'ConsumerServices',
          label: {
            [languageSelectOptions.en.value]: 'Consumer Services',
            [languageSelectOptions.fr.value]: 'Services aux consommateurs',
          },
        },
        {
          key: 'Education',
          value: 'Education',
          label: {
            [languageSelectOptions.en.value]: 'Education',
            [languageSelectOptions.fr.value]: 'Éducation',
          },
        },
        {
          key: 'Entertainment',
          value: 'Entertainment',
          label: {
            [languageSelectOptions.en.value]: 'Entertainment',
            [languageSelectOptions.fr.value]: 'Divertissement',
          },
        },
        {
          key: 'LightIndustrialWarehousing',
          value: 'LightIndustrialWarehousing',
          label: {
            [languageSelectOptions.en.value]: 'Light industrial / Warehousing',
            [languageSelectOptions.fr.value]: 'Industrie légère / Entreposage',
          },
        },
      ],
    },
  },
];

export const sortConfig = {
  // Enable/disable the sorting control in the SearchPage
  active: true,

  // Note: queryParamName 'sort' is fixed,
  // you can't change it since Flex API expects it to be named as 'sort'
  queryParamName: 'sort',

  // Internal key for the relevance option, see notes below.
  relevanceKey: 'relevance',

  // Keyword filter is sorting the results already by relevance.
  // If keyword filter is active, we need to disable sorting.
  conflictingFilters: ['keyword'],

  options: [
    { key: 'createdAt', label: 'Newest' },
    { key: '-createdAt', label: 'Oldest' },
    { key: '-price', label: 'Lowest price' },
    { key: 'price', label: 'Highest price' },

    // The relevance is only used for keyword search, but the
    // parameter isn't sent to the Marketplace API. The key is purely
    // for handling the internal state of the sorting dropdown.
    {
      key: 'relevance',
      label: 'Relevance',
      longLabel: 'Relevance (Keyword search)',
    },
  ],
};
export const phoneDialCodeData = [
  {
    name: {
      en: 'Canada',
      fr: 'Canada',
    },
    iso2: 'CA',
    iso3: 'CAN',
    dialCode: '+1',
    maxDigit: 10,
    nddPrefix: '1',
  },

  {
    name: {
      en: 'India',
      fr: 'Inde',
    },
    iso2: 'IN',
    iso3: 'IND',
    dialCode: '+91',
    maxDigit: 10,
    nddPrefix: '0',
  },

  {
    name: {
      en: 'United States',
      fr: 'États-Unis',
    },
    iso2: 'US',
    iso3: 'USA',
    dialCode: '+1',
    maxDigit: 10,
    nddPrefix: '1',
  },
  {
    name: {
      en: 'United States Minor Outlying Islands',
      fr: 'Îles mineures éloignées des États-Unis',
    },
    iso2: 'UM',
    iso3: 'UMI',
    dialCode: '+1',
    maxDigit: 10,
  },
];

export const defaultPhonecodeCountry = {
  name: 'Canada',
  iso2: 'CA',
  iso3: 'CAN',
  dialCode: '+1',
  maxDigit: 10,
  nddPrefix: '1',
};

export const modalPosition = {
  left: 'left',
  top: 'top',
  right: 'right',
  bottom: 'bottom',
  center: 'center',
};
export const hourOptions = [
  {
    value: '0',
    label: {
      [languageSelectOptions.en.value]: '00',
      [languageSelectOptions.fr.value]: '00',
    },
  },
  {
    value: '1',
    label: {
      [languageSelectOptions.en.value]: '01',
      [languageSelectOptions.fr.value]: '01',
    },
  },
  {
    value: '2',
    label: {
      [languageSelectOptions.en.value]: '02',
      [languageSelectOptions.fr.value]: '02',
    },
  },
  {
    value: '3',
    label: {
      [languageSelectOptions.en.value]: '03',
      [languageSelectOptions.fr.value]: '03',
    },
  },
  {
    value: '4',
    label: {
      [languageSelectOptions.en.value]: '04',
      [languageSelectOptions.fr.value]: '04',
    },
  },
  {
    value: '5',
    label: {
      [languageSelectOptions.en.value]: '05',
      [languageSelectOptions.fr.value]: '05',
    },
  },
  {
    value: '6',
    label: {
      [languageSelectOptions.en.value]: '06',
      [languageSelectOptions.fr.value]: '06',
    },
  },
  {
    value: '7',
    label: {
      [languageSelectOptions.en.value]: '07',
      [languageSelectOptions.fr.value]: '07',
    },
  },
  {
    value: '8',
    label: {
      [languageSelectOptions.en.value]: '08',
      [languageSelectOptions.fr.value]: '08',
    },
  },
  {
    value: '9',
    label: {
      [languageSelectOptions.en.value]: '09',
      [languageSelectOptions.fr.value]: '09',
    },
  },
  {
    value: '10',
    label: {
      [languageSelectOptions.en.value]: '10',
      [languageSelectOptions.fr.value]: '10',
    },
  },
  {
    value: '11',
    label: {
      [languageSelectOptions.en.value]: '11',
      [languageSelectOptions.fr.value]: '11',
    },
  },
  {
    value: '12',
    label: {
      [languageSelectOptions.en.value]: '12',
      [languageSelectOptions.fr.value]: '12',
    },
  },
  {
    value: '13',
    label: {
      [languageSelectOptions.en.value]: '13',
      [languageSelectOptions.fr.value]: '13',
    },
  },
  {
    value: '14',
    label: {
      [languageSelectOptions.en.value]: '14',
      [languageSelectOptions.fr.value]: '14',
    },
  },
  {
    value: '15',
    label: {
      [languageSelectOptions.en.value]: '15',
      [languageSelectOptions.fr.value]: '15',
    },
  },
  {
    value: '16',
    label: {
      [languageSelectOptions.en.value]: '16',
      [languageSelectOptions.fr.value]: '16',
    },
  },
  {
    value: '17',
    label: {
      [languageSelectOptions.en.value]: '17',
      [languageSelectOptions.fr.value]: '17',
    },
  },
  {
    value: '18',
    label: {
      [languageSelectOptions.en.value]: '18',
      [languageSelectOptions.fr.value]: '18',
    },
  },
  {
    value: '19',
    label: {
      [languageSelectOptions.en.value]: '19',
      [languageSelectOptions.fr.value]: '19',
    },
  },
  {
    value: '20',
    label: {
      [languageSelectOptions.en.value]: '20',
      [languageSelectOptions.fr.value]: '20',
    },
  },
  {
    value: '21',
    label: {
      [languageSelectOptions.en.value]: '21',
      [languageSelectOptions.fr.value]: '21',
    },
  },
  {
    value: '22',
    label: {
      [languageSelectOptions.en.value]: '22',
      [languageSelectOptions.fr.value]: '22',
    },
  },
  {
    value: '23',
    label: {
      [languageSelectOptions.en.value]: '23',
      [languageSelectOptions.fr.value]: '23',
    },
  },
];

export const UnpaidBreakOptions = [
  {
    value: '00',
    label: {
      [languageSelectOptions.en.value]: '00 Minutes',
      [languageSelectOptions.fr.value]: '00 Minutes',
    },
  },
  {
    value: '15',
    label: {
      [languageSelectOptions.en.value]: '15 Minutes',
      [languageSelectOptions.fr.value]: '15 Minutes',
    },
  },
  {
    value: '30',
    label: {
      [languageSelectOptions.en.value]: '30 Minutes',
      [languageSelectOptions.fr.value]: '30 Minutes',
    },
  },
  {
    value: '45',
    label: {
      [languageSelectOptions.en.value]: '45 Minutes',
      [languageSelectOptions.fr.value]: '45 Minutes',
    },
  },
  {
    value: '60',
    label: {
      [languageSelectOptions.en.value]: '60 Minutes',
      [languageSelectOptions.fr.value]: '60 Minutes',
    },
  },
];
export const minuteOptions = [
  {
    value: '00',
    label: {
      [languageSelectOptions.en.value]: '00',
      [languageSelectOptions.fr.value]: '00',
    },
  },
  {
    value: '15',
    label: {
      [languageSelectOptions.en.value]: '15 ',
      [languageSelectOptions.fr.value]: '15 ',
    },
  },
  {
    value: '30',
    label: {
      [languageSelectOptions.en.value]: '30 ',
      [languageSelectOptions.fr.value]: '30 ',
    },
  },
  {
    value: '45',
    label: {
      [languageSelectOptions.en.value]: '45 ',
      [languageSelectOptions.fr.value]: '45 ',
    },
  },
];

export const recurringInterval = [
  {
    value: 'Daily',
    label: {
      [languageSelectOptions.en.value]: 'Daily',
      [languageSelectOptions.fr.value]: 'Quotidien',
    },
  },
  {
    value: 'Weekly',
    label: {
      [languageSelectOptions.en.value]: 'Weekly',
      [languageSelectOptions.fr.value]: 'Hebdomadaire',
    },
  },
];

export const recurringDaysOptions = [
  {
    key: 'Sunday',

    value: 'Sunday',
    label: {
      [languageSelectOptions.en.value]: 'SUN',
      [languageSelectOptions.fr.value]: 'Dimanche',
    },
  },

  {
    key: 'Monday',

    value: 'Monday',
    label: {
      [languageSelectOptions.en.value]: 'MON',
      [languageSelectOptions.fr.value]: 'Lundi',
    },
  },

  {
    key: 'Tuesday',

    value: 'Tuesday',
    label: {
      [languageSelectOptions.en.value]: 'TUE',
      [languageSelectOptions.fr.value]: 'Mardi',
    },
  },
  {
    key: 'Wednesday',

    value: 'Wednesday',
    label: {
      [languageSelectOptions.en.value]: 'WED',
      [languageSelectOptions.fr.value]: 'Mercredi',
    },
  },

  {
    key: 'Thursday',

    value: 'Thursday',
    label: {
      [languageSelectOptions.en.value]: 'THR',
      [languageSelectOptions.fr.value]: 'Jeudi',
    },
  },
  {
    key: 'Friday',

    value: 'Friday',
    label: {
      [languageSelectOptions.en.value]: 'FRI',
      [languageSelectOptions.fr.value]: 'Vendredi',
    },
  },
  {
    key: 'Saturday',

    value: 'Saturday',
    label: {
      [languageSelectOptions.en.value]: 'SAT',
      [languageSelectOptions.fr.value]: 'Samedi',
    },
  },
];

export const jobInstrucionOptions = [
  {
    key: 'Safetyshoes',

    value: 'Safetyshoes',
    label: {
      [languageSelectOptions.en.value]: 'Safety shoes',
      [languageSelectOptions.fr.value]: 'Chaussures de sécurité',
    },
  },

  {
    key: 'WhiteshirtBlackPants',

    value: 'WhiteshirtBlackPants',
    label: {
      [languageSelectOptions.en.value]: 'White shirt and black pants',
      [languageSelectOptions.fr.value]: 'Chemise blanche et pantalon noir',
    },
  },

  {
    key: 'NonSlipShoes',

    value: 'NonSlipShoes',
    label: {
      [languageSelectOptions.en.value]: 'Non-slip shoes',
      [languageSelectOptions.fr.value]: 'Chaussures antidérapantes',
    },
  },

  {
    key: 'DrivingLicense',

    value: 'DrivingLicense',
    label: {
      [languageSelectOptions.en.value]: 'Driving license',
      [languageSelectOptions.fr.value]: 'Permis de conduire',
    },
  },
  {
    key: 'lift25Pounds',

    value: 'lift25Pounds',
    label: {
      [languageSelectOptions.en.value]: 'Able to lift more than 25 pounds',
      [languageSelectOptions.fr.value]: 'Capable de soulever plus de 25 livres',
    },
  },
];

export const groomingPresetsOptions = [
  {
    key: 'Elevated',

    value: 'Elevated',
    label: {
      [languageSelectOptions.en.value]: 'Elevated',
      [languageSelectOptions.fr.value]: 'Élevé',
    },
  },

  {
    key: 'Professional',

    value: 'Professional',
    label: {
      [languageSelectOptions.en.value]: 'Professional',
      [languageSelectOptions.fr.value]: 'Professionnel',
    },
  },

  {
    key: 'Standard',

    value: 'Standard',
    label: {
      [languageSelectOptions.en.value]: 'Standard',
      [languageSelectOptions.fr.value]: 'Standard',
    },
  },
  {
    key: 'CustomGrooming',

    value: 'CustomGrooming',
    label: {
      [languageSelectOptions.en.value]: 'Custom Grooming',
      [languageSelectOptions.fr.value]: 'Toilettage personnalisé',
    },
  },

  {
    key: 'NoPreference',

    value: 'NoPreference',
    label: {
      [languageSelectOptions.en.value]: 'No Preference',
      [languageSelectOptions.fr.value]: 'Pas de préférence',
    },
  },
  {
    key: 'NoJeans',

    value: 'NoJeans',
    label: {
      [languageSelectOptions.en.value]: 'No Jeans',
      [languageSelectOptions.fr.value]: 'Pas de jean',
    },
  },
];

export const riskFactorOptions = [
  {
    key: 'ChemicalRisks',

    value: 'ChemicalRisks',
    label: {
      [languageSelectOptions.en.value]: 'Chemical Risks',
      [languageSelectOptions.fr.value]: 'Risques associés aux produits chimiques',
    },
    desc: {
      [languageSelectOptions.en.value]:
        'All raw materials and by-product of a process or product resulting for mechanical action, evaporation, combustion, decomposition or chemical reaction.',
      [languageSelectOptions.fr
        .value]: `Toutes les matières premières et les sous-produits d'un procédé ou d'un produit résultant d'une action mécanique, d'évaporation, de combustion, de décomposition ou de réaction chimique.`,
    },
  },

  {
    key: 'BiologicalRisks',

    value: 'BiologicalRisks',
    label: {
      [languageSelectOptions.en.value]: 'Biological Risks',
      [languageSelectOptions.fr.value]: 'Risques biologiques',
    },
    desc: {
      [languageSelectOptions.en.value]:
        'Risk livings organism such as plants, animals and biological agents (viruses, bacterias, parasites, fung) that can have health effect.',
      [languageSelectOptions.fr
        .value]: `Risques provenant d’organismes vivants tels que les plantes, les animaux et les agents biologiques (virus, bactéries, parasites, champignons) qui peuvent avoir des effets sur la santé.`,
    },
  },

  {
    key: 'ErgonomicRisks',

    value: 'ErgonomicRisks',
    label: {
      [languageSelectOptions.en.value]: 'Ergonomic Risks',
      [languageSelectOptions.fr.value]: 'Risques ergonomiques',
    },
    desc: {
      [languageSelectOptions.en.value]:
        'Repetitive tasks, use of equipment not adapted to its design, excessive effort or static posture.',
      [languageSelectOptions.fr
        .value]: `Tâches répétitives, utilisation d'équipements non adaptés à leur conception, effort excessif ou posture statique.`,
    },
  },

  {
    key: 'PhysicalRisks',

    value: 'PhysicalRisks',
    label: {
      [languageSelectOptions.en.value]: 'Physical Risks',
      [languageSelectOptions.fr.value]: 'Risques physiques',
    },
    desc: {
      [languageSelectOptions.en.value]:
        'Forms of energy or forces such as noise, vibration, electricity, pressure and radiation.',
      [languageSelectOptions.fr
        .value]: `Formes d'énergie ou de forces telles que le bruit, les vibrations, l'électricité, la pression et le rayonnement.`,
    },
  },
  {
    key: 'PsychologicalRisks',

    value: 'PsychologicalRisks',
    label: {
      [languageSelectOptions.en.value]: 'Psychological Risks',
      [languageSelectOptions.fr.value]: 'Risques psychologiques',
    },
    desc: {
      [languageSelectOptions.en.value]:
        'Factors related to the nature or organization of the work such as harassment, violence and aggression, role ambiguity, lack of respect, heavy workload, high work pace, task complexity and inadequate training.',
      [languageSelectOptions.fr
        .value]: `Facteurs liés à la nature ou à l'organisation du travail tels que le harcèlement, la violence et l'agression, l'ambiguïté des rôles, le manque de respect, la charge de travail importante, le rythme de travail élevé, la complexité des tâches et la formation inadéquate.`,
    },
  },
  {
    key: 'SafetyRelatedRisks',

    value: 'SafetyRelatedRisks',
    label: {
      [languageSelectOptions.en.value]: 'Safety Related Risks',
      [languageSelectOptions.fr.value]: 'Risques liés à la sécurité',
    },
    desc: {
      [languageSelectOptions.en.value]:
        'Moving parts of machines and equipment, sharp edges, shape of pieces and materials, handling tools and equipment, working at heights or in confined spaces, slippery or uneven floors, vehicles, aggressive customers, projection of materials, inadequate mechanical resistance, fires and explosions.',
      [languageSelectOptions.fr
        .value]: `Pièces mobiles des machines et équipements, arêtes vives, forme des pièces et matériaux, manipulation d'outils et d’équipements, travail en hauteur ou dans des espaces confinés, sols glissants ou inégaux, véhicules, clients agressifs, projection de matériaux, résistance mécanique inadéquate, incendies et explosions.`,
    },
  },
];

export const sosAdOptions = [
  {
    value: 'sosWorker',
    label: {
      [languageSelectOptions.en.value]: 'A Djobworker',
      [languageSelectOptions.fr.value]: 'Un collaborateur Djob',
    },
  },
  {
    value: 'friend',
    label: {
      [languageSelectOptions.en.value]: 'A friend / family / colleague',
      [languageSelectOptions.fr.value]: 'Un ami / famille / collègue',
    },
  },
  {
    value: 'sosSalesRep',
    label: {
      [languageSelectOptions.en.value]: 'Djob sales representative',
      [languageSelectOptions.fr.value]: 'Représentant commercial Djob',
    },
  },
  {
    value: 'onlineAd',
    label: {
      [languageSelectOptions.en.value]: 'Online advertising',
      [languageSelectOptions.fr.value]: 'Publicité en ligne',
    },
  },
  {
    value: 'internet',
    label: {
      [languageSelectOptions.en.value]: 'Searching on the internet',
      [languageSelectOptions.fr.value]: 'Recherche sur internet',
    },
  },
  {
    value: 'directMail',
    label: {
      [languageSelectOptions.en.value]: 'Direct mail',
      [languageSelectOptions.fr.value]: 'Courrier direct',
    },
  },
  {
    value: 'socialMedia',
    label: {
      [languageSelectOptions.en.value]: 'Social Media',
      [languageSelectOptions.fr.value]: 'Réseaux sociaux',
    },
  },
  {
    value: 'other',
    label: {
      [languageSelectOptions.en.value]: 'Other',
      [languageSelectOptions.fr.value]: 'Autre',
    },
  },
];

export const listingTypes = {
  accommodationServices: {
    value: 'accommodationServices',
    label: {
      [languageSelectOptions.en.value]: 'Administrative/Clerical',
      [languageSelectOptions.fr.value]: `Administratif/ Travail de bureau`,
    },
  },

  administrativeSupportService: {
    value: 'administrativeSupportService',
    label: {
      [languageSelectOptions.en.value]: 'Hotels, Restaurants & Events',
      [languageSelectOptions.fr.value]: 'Hôtellerie, restauration et événements',
    },
  },
  construction: {
    value: 'construction',
    label: {
      [languageSelectOptions.en.value]: 'Logistic & Production',
      [languageSelectOptions.fr.value]: 'Logistique et production',
    },
  },
  consumerServices: {
    value: 'consumerServices',
    label: {
      [languageSelectOptions.en.value]: 'Retail',
      [languageSelectOptions.fr.value]: 'Vente au détail',
    },
  },
  education: {
    value: 'education',
    label: {
      [languageSelectOptions.en.value]: 'Other',
      [languageSelectOptions.fr.value]: 'Autre',
    },
  },

};

export const countryPostalCode = [
  {
    iso2: 'US',
    iso3: 'USA',
    name: 'United States',
    postalCodeRegexStr: '\\d\\d\\d\\d\\d-\\d\\d\\d\\d|\\d\\d\\d\\d\\d',
  },
  {
    iso2: 'CA',
    iso3: 'CAN',
    name: 'Canada',
    postalCodeRegexStr: '\\w\\d\\w\\s\\d\\w\\d|\\w\\d\\w-\\d\\w\\d',
  },
  {
    iso2: 'GB',
    iso3: 'GBR',
    name: 'United Kingdom',
    postalCodeRegexStr: '\\w\\w\\d\\d\\s\\d\\w\\w|\\w\\w\\w\\s\\d\\w\\w|\\w\\w\\d\\s\\d\\w\\w',
  },
  {
    iso2: 'AR',
    iso3: 'ARG',
    name: 'Argentina',
    postalCodeRegexStr: '\\w\\d\\d\\d\\d\\w\\w\\w',
  },
  {
    iso2: 'AU',
    iso3: 'AUS',
    name: 'Australia',
    postalCodeRegexStr: '\\d\\d\\d\\d',
  },
  {
    iso2: 'AT',
    iso3: 'AUT',
    name: 'Austria',
    postalCodeRegexStr: '\\d\\d\\d\\d',
  },
  {
    iso2: 'BE',
    iso3: 'BEL',
    name: 'Belgium',
    postalCodeRegexStr: '\\d\\d\\d\\d',
  },
  {
    iso2: 'BR',
    iso3: 'BRA',
    name: 'Brazil',
    postalCodeRegexStr: '\\d\\d\\d\\d\\d-\\d\\d\\d|\\d\\d\\d\\d\\d',
  },
  {
    iso2: 'BG',
    iso3: 'BGR',
    name: 'Bulgaria',
    postalCodeRegexStr: '\\d\\d\\d\\d',
  },
  {
    iso2: 'KH',
    iso3: 'KHM',
    name: 'Cambodia',
    postalCodeRegexStr: '\\d\\d\\d\\d\\d',
  },
  {
    iso2: 'CL',
    iso3: 'CHL',
    name: 'Chile',
    postalCodeRegexStr: '\\d\\d\\d-\\d\\d\\d\\d|\\d\\d\\d\\d\\d\\d\\d',
  },
  {
    iso2: 'CO',
    iso3: 'COL',
    name: 'Colombia',
    postalCodeRegexStr: '\\d\\d\\d\\d\\d\\d',
  },
  {
    iso2: 'HR',
    iso3: 'HRV',
    name: 'Croatia',
    postalCodeRegexStr: '\\d\\d\\d\\d\\d',
  },
  {
    iso2: 'CR',
    iso3: 'CRI',
    name: 'Costa Rica',
    postalCodeRegexStr: '\\d\\d\\d\\d\\d-\\d\\d\\d\\d|\\d\\d\\d\\d\\d',
  },
  {
    iso2: 'CZ',
    iso3: 'CZE',
    name: 'Czech Republic',
    postalCodeRegexStr: '\\d\\d\\d\\s\\d\\d|\\d\\d\\d-\\d\\d|\\d\\d\\d\\d\\d',
  },
  {
    iso2: 'DK',
    iso3: 'DNK',
    name: 'Denmark',
    postalCodeRegexStr: '\\d\\d\\d\\d|\\d\\d',
  },
  {
    iso2: 'DO',
    iso3: 'DOM',
    name: 'Dominican Republic',
    postalCodeRegexStr: '\\d\\d\\d\\d\\d',
  },
  {
    iso2: 'EC',
    iso3: 'ECU',
    name: 'Ecuador',
    postalCodeRegexStr: '\\d\\d\\d\\d\\d\\d',
  },
  {
    iso2: 'EG',
    iso3: 'EGY',
    name: 'Egypt',
    postalCodeRegexStr: '\\d\\d\\d\\d\\d',
  },
  {
    iso2: 'EE',
    iso3: 'EST',
    name: 'Estonia',
    postalCodeRegexStr: '\\d\\d\\d\\d\\d',
  },
  {
    iso2: 'FI',
    iso3: 'FIN',
    name: 'Finland',
    postalCodeRegexStr: '\\d\\d\\d\\d\\d',
  },
  {
    iso2: 'FR',
    iso3: 'FRA',
    name: 'France',
    postalCodeRegexStr: '\\d\\d\\d\\d\\d',
  },
  {
    iso2: 'DE',
    iso3: 'DEU',
    name: 'Germany',
    postalCodeRegexStr: '\\d\\d\\d\\d\\d',
  },
  {
    iso2: 'GR',
    iso3: 'GRC',
    name: 'Greece',
    postalCodeRegexStr: '\\d\\d\\s\\d\\d',
  },
  {
    iso2: 'GT',
    iso3: 'GTM',
    name: 'Guatemala',
    postalCodeRegexStr: '\\d\\d\\d\\d\\d',
  },
  {
    iso2: 'HU',
    iso3: 'HUN',
    name: 'Hungary',
    postalCodeRegexStr: '\\d\\d\\d\\d',
  },
  {
    iso2: 'IN',
    iso3: 'IND',
    name: 'India',
    postalCodeRegexStr: '\\d\\d\\d\\s\\d\\d\\d|\\d\\d\\d\\d\\d\\d',
  },
  {
    iso2: 'ID',
    iso3: 'IDN',
    name: 'Indonesia',
    postalCodeRegexStr: '\\d\\d\\d\\d\\d',
  },
  {
    iso2: 'IE',
    iso3: 'IRL',
    name: 'Ireland',
    postalCodeRegexStr:
      '\\w\\d\\d\\s\\w\\d\\w\\d|\\w\\d\\d\\s\\w\\w\\d\\d|\\w\\d\\d\\s\\w\\d\\w\\w',
  },
  {
    iso2: 'IT',
    iso3: 'ITA',
    name: 'Italy',
    postalCodeRegexStr: '\\d\\d\\d\\d\\d',
  },
  {
    iso2: 'JO',
    iso3: 'JOR',
    name: 'Jordan',
    postalCodeRegexStr: '\\d\\d\\d\\d\\d',
  },
  {
    name: 'Japan',
    iso2: 'JP',
    iso3: 'JPN',
    postalCodeRegexStr: '\\d\\d\\d-\\d\\d\\d\\d',
  },
  {
    iso2: 'KW',
    iso3: 'KWT',
    name: 'Kuwait',
    postalCodeRegexStr: '\\d\\d\\d\\d\\d',
  },
  {
    iso2: 'LV',
    iso3: 'LVA',
    name: 'Latvia',
    postalCodeRegexStr: '\\d\\d\\d\\d',
  },
  {
    iso2: 'LT',
    iso3: 'LTU',
    name: 'Lithuania',
    postalCodeRegexStr: '\\d\\d\\d\\d\\d',
  },
  {
    iso2: 'LU',
    iso3: 'LUX',
    name: 'Luxembourg',
    postalCodeRegexStr: '\\d\\d\\d\\d\\d',
  },
  {
    iso2: 'MY',
    iso3: 'MYS',
    name: 'Malaysia',
    postalCodeRegexStr: '\\d\\d\\d\\d\\d',
  },
  {
    iso2: 'MT',
    iso3: 'MLT',
    name: 'Malta',
    postalCodeRegexStr: '\\w\\w\\w\\s\\d\\d\\d\\d',
  },
  {
    iso2: 'MX',
    iso3: 'MEX',
    name: 'Mexico',
    postalCodeRegexStr: '\\d\\d\\d\\d\\d',
  },
  {
    iso2: 'ME',
    iso3: 'MNE',
    name: 'Montenegro',
    postalCodeRegexStr: '\\d\\d\\d\\d\\d',
  },
  {
    iso2: 'NL',
    iso3: 'NLD',
    name: 'Netherlands',
    postalCodeRegexStr: '\\d\\d\\d\\d\\s\\w\\w|\\d\\d\\d\\d\\w\\w',
  },
  {
    iso2: 'NZ',
    iso3: 'NZL',
    name: 'New Zealand',
    postalCodeRegexStr: '\\d\\d\\d\\d',
  },
  {
    iso2: 'NO',
    iso3: 'NOR',
    name: 'Norway',
    postalCodeRegexStr: '\\d\\d\\d\\d',
  },
  {
    iso2: 'OM',
    iso3: 'OMN',
    name: 'Oman',
    postalCodeRegexStr: '\\d\\d\\d',
  },
  {
    iso2: 'PK',
    iso3: 'PAK',
    name: 'Pakistan',
    postalCodeRegexStr: '\\d\\d\\d\\d\\d',
  },
  {
    iso2: 'PY',
    iso3: 'PRY',
    name: 'Paraguay',
    postalCodeRegexStr: '\\d\\d\\d\\d',
  },
  {
    iso2: 'PE',
    iso3: 'PER',
    name: 'Peru',
    postalCodeRegexStr: '\\d\\d\\d\\d\\d',
  },
  {
    iso2: 'PH',
    iso3: 'PHL',
    name: 'Philippines',
    postalCodeRegexStr: '\\d\\d\\d\\d',
  },
  {
    iso2: 'PL',
    iso3: 'POL',
    name: 'Poland',
    postalCodeRegexStr: '\\d\\d-\\d\\d\\d',
  },
  {
    iso2: 'PT',
    iso3: 'PRT',
    name: 'Portugal',
    postalCodeRegexStr: '\\d\\d\\d\\d-\\d\\d\\d',
  },
  {
    iso2: 'RO',
    iso3: 'ROU',
    name: 'Romania',
    postalCodeRegexStr: '\\d\\d\\d\\d\\d\\d',
  },
  {
    iso2: 'RU',
    iso3: 'RUS',
    name: 'Russia',
    postalCodeRegexStr: '\\d\\d\\d\\d\\d\\d',
  },
  {
    iso2: 'SA',
    iso3: 'SAU',
    name: 'Saudi Arabia',
    postalCodeRegexStr: '\\d\\d\\d\\d\\d-\\d\\d\\d\\d',
  },
  {
    iso2: 'RS',
    iso3: 'SRB',
    name: 'Serbia',
    postalCodeRegexStr: '\\d\\d\\d\\d\\d',
  },
  {
    iso2: 'SG',
    iso3: 'SGP',
    name: 'Singapore',
    postalCodeRegexStr: '\\d\\d\\d\\d\\d\\d',
  },
  {
    iso2: 'SK',
    iso3: 'SVK',
    name: 'Slovakia',
    postalCodeRegexStr: '\\d\\d\\d\\s\\d\\d|\\d\\d\\d\\d\\d',
  },
  {
    iso2: 'SI',
    iso3: 'SVN',
    name: 'Slovenia',
    postalCodeRegexStr: '\\d\\d\\d\\d',
  },
  {
    iso2: 'ZA',
    iso3: 'ZAF',
    name: 'South Africa',
    postalCodeRegexStr: '\\d\\d\\d\\d',
  },
  {
    iso2: 'KR',
    iso3: 'KOR',
    name: 'South Korea',
    postalCodeRegexStr: '\\d\\d\\d\\d\\d',
  },
  {
    iso2: 'ES',
    iso3: 'ESP',
    name: 'Spain',
    postalCodeRegexStr: '\\d\\d\\d\\d\\d',
  },
  {
    iso2: 'SE',
    iso3: 'SWE',
    name: 'Sweden',
    postalCodeRegexStr:
      '\\w\\w-\\d\\d\\d-\\d\\d|\\d\\d\\d-\\d\\d|\\d\\d\\d\\s\\d\\d|\\d\\d\\d\\d\\d',
  },
  {
    iso2: 'CH',
    iso3: 'CHE',
    name: 'Switzerland',
    postalCodeRegexStr: '\\d\\d\\d\\d',
  },
  {
    iso2: 'TW',
    iso3: 'TWN',
    name: 'Taiwan',
    postalCodeRegexStr: '\\d\\d\\d-\\d\\d\\d|\\d\\d\\d\\d\\d\\d|\\d\\d\\d',
  },
  {
    iso2: 'TZ',
    iso3: 'TZA',
    name: 'Tanzania',
    postalCodeRegexStr: '\\d\\d\\d\\d\\d',
  },
  {
    iso2: 'TH',
    iso3: 'THA',
    name: 'Thailand',
    postalCodeRegexStr: '\\d\\d\\d\\d\\d',
  },
  {
    iso2: 'TT',
    iso3: 'TTO',
    name: 'Trinidad and Tobago',
    postalCodeRegexStr: '\\d\\d\\d\\d\\d\\d',
  },
  {
    iso2: 'TN',
    iso3: 'TUN',
    name: 'Tunisia',
    postalCodeRegexStr: '\\d\\d\\d\\d',
  },
  {
    iso2: 'TR',
    iso3: 'TUR',
    name: 'Turkey',
    postalCodeRegexStr: '\\d\\d\\d\\d\\d',
  },
  {
    iso2: 'UA',
    iso3: 'UKR',
    name: 'Ukraine',
    postalCodeRegexStr: '\\d\\d\\d\\d\\d',
  },
  {
    iso2: 'UY',
    iso3: 'URY',
    name: 'Uruguay',
    postalCodeRegexStr: '\\d\\d\\d\\d\\d',
  },
  {
    iso2: 'VE',
    iso3: 'VEN',
    name: 'Venezuela',
    postalCodeRegexStr: '\\d\\d\\d\\d-\\w|\\d\\d\\d\\d',
  },
  {
    iso2: 'VN',
    iso3: 'VNM',
    name: 'Vietnam',
    postalCodeRegexStr: '\\d\\d\\d\\d\\d\\d',
  },
];

export const defaultPostalCodeRegexStr = '\\d\\d\\d\\d';

export const customIdsKeyName = {
  business: 'business',
  worker: 'worker',
  listing: 'listing',
  guest: 'guest',
};

export const workerAllowedOptions = {
  yes: {
    value: 'yes',
    label: { [languageSelectOptions.en.value]: 'YES', [languageSelectOptions.fr.value]: 'Oui' },
  },
  no: {
    value: 'no',
    label: { [languageSelectOptions.en.value]: 'NO', [languageSelectOptions.fr.value]: 'Non' },
  },
};
export const contactAuthOptions = {
  yes: {
    value: 'yes',
    label: { [languageSelectOptions.en.value]: 'YES', [languageSelectOptions.fr.value]: 'Oui' },
  },
  no: {
    value: 'no',
    label: { [languageSelectOptions.en.value]: 'NO', [languageSelectOptions.fr.value]: 'Non' },
  },
};

export const gigEnabledOptions = [
  {
    value: 'true',
    label: { [languageSelectOptions.en.value]: 'YES', [languageSelectOptions.fr.value]: 'Oui' },
  },
  {
    value: 'false',
    label: { [languageSelectOptions.en.value]: 'NO', [languageSelectOptions.fr.value]: 'Non' },
  },
];

export const drivingLicenseOptions = {
  yes: {
    value: 'yes',
    label: { [languageSelectOptions.en.value]: 'YES', [languageSelectOptions.fr.value]: 'Oui' },
  },
  no: {
    value: 'no',
    label: { [languageSelectOptions.en.value]: 'NO', [languageSelectOptions.fr.value]: 'Non' },
  },
};

export const age16PlusOptions = {
  yes: {
    value: 'yes',
    label: { [languageSelectOptions.en.value]: 'YES', [languageSelectOptions.fr.value]: 'Oui' },
  },
  no: {
    value: 'no',
    label: { [languageSelectOptions.en.value]: 'NO', [languageSelectOptions.fr.value]: 'Non' },
  },
};

export const momentUnitType = {
  years: 'years',
  quarters: 'quarters',
  months: 'months',
  days: 'days',
  hours: 'hours',
  minutes: 'minutes',
  seconds: 'seconds',
  milliseconds: 'milliseconds',
};

export const radioButtonType = {
  filled: 'filled',
  tick: 'tick',
};

export const employmentType = {
  fullTime: {
    value: 'fullTime',
    label: {
      [languageSelectOptions.en.value]: 'Full Time',
      [languageSelectOptions.fr.value]: 'À temps plein',
    },
  },
  partTime: {
    value: 'partTime',
    label: {
      [languageSelectOptions.en.value]: 'Part Time',
      [languageSelectOptions.fr.value]: 'À temps partiel',
    },
  },
  // internship: {
  //   value: 'internship',
  //   label: {
  //     [languageSelectOptions.en.value]: 'Internship',
  //     [languageSelectOptions.fr.value]: 'Stage',
  //   },
  // },
};

export const minimumAgeRequired = 16; // minimum age 16 years

export const workingStatus = {
  currentlyWorking: 'currentlyWorking',
  leftCompany: 'leftCompany',
};

export const preferredLang = {
  [languageSelectOptions.en.value]: {
    value: 'en',
    label: {
      [languageSelectOptions.en.value]: 'English',
      [languageSelectOptions.fr.value]: 'Anglais',
    },
  },
  [languageSelectOptions.fr.value]: {
    value: 'fr',
    label: {
      [languageSelectOptions.en.value]: 'French',
      [languageSelectOptions.fr.value]: 'Français',
    },
  },
};

export const NATIVE_LANG_STORAGE_KEY = 'NativeLanguage';

export const LOGIN = 'login';
export const SIGN_UP = 'signup';
export const CONFIRM = 'confirm';
export const COMPANY_SIGN_UP = 'signupCompany';
export const WORKER_SIGN_UP = 'signupWorker';

export const ALERT_RED = 'red';
export const ALERT_YELLOW = 'yellow';
export const ALERT_GREEN = 'green';

export const USER_TYPE = 'USER_TYPE';
export const minimumLevelOptions = [
  {
    value: 'beginner',
    label: {
      [languageSelectOptions.en.value]: 'Beginner',
      [languageSelectOptions.fr.value]: 'Débutant',
    },
  },
  {
    value: 'intermediate',
    label: {
      [languageSelectOptions.en.value]: 'Intermediate',
      [languageSelectOptions.fr.value]: 'Intermédiaire',
    },
  },
  // {
  //   value: 'medium',
  //   label: {
  //     [languageSelectOptions.en.value]: 'Medium',
  //     [languageSelectOptions.fr.value]: 'Moyen',
  //   },
  // },
  {
    value: 'expert',
    label: {
      [languageSelectOptions.en.value]: 'Expert',
      [languageSelectOptions.fr.value]: 'Expert',
    },
  },

  {
    value: 'motherTongue',
    label: {
      [languageSelectOptions.en.value]: 'Mother Tongue',
      [languageSelectOptions.fr.value]: 'Langue maternelle',
    },
  },
];

export const languageOption = [
  {
    value: 'Abkhaz',
    label: {
      [languageSelectOptions.en.value]: 'Abkhaz',
      [languageSelectOptions.fr.value]: 'Abkhaze',
    },
    local: 'Аҧсуа',
  },
  {
    value: 'Afar',
    label: {
      [languageSelectOptions.en.value]: 'Afar',
      [languageSelectOptions.fr.value]: 'Afar',
    },
    local: 'Afaraf',
  },
  {
    value: 'Afrikaans',

    label: {
      [languageSelectOptions.en.value]: 'Afrikaans',
      [languageSelectOptions.fr.value]: 'Afrikaans',
    },
    local: 'Afrikaans',
  },
  {
    value: 'Akan',
    label: {
      [languageSelectOptions.en.value]: 'Akan',
      [languageSelectOptions.fr.value]: 'Akan',
    },
    local: 'Akan',
  },
  {
    value: 'Albanian',

    label: {
      [languageSelectOptions.en.value]: 'Albanian',
      [languageSelectOptions.fr.value]: 'Albanais',
    },
    local: 'Shqip',
  },
  {
    value: 'Amharic',

    label: {
      [languageSelectOptions.en.value]: 'Amharic',
      [languageSelectOptions.fr.value]: 'Amharique',
    },
    local: 'አማርኛ',
  },
  {
    value: 'Arabic',

    label: {
      [languageSelectOptions.en.value]: 'Arabic',
      [languageSelectOptions.fr.value]: 'Arabe',
    },
    local: 'العربية',
  },
  {
    value: 'Aragonese',

    label: {
      [languageSelectOptions.en.value]: 'Aragonese',
      [languageSelectOptions.fr.value]: 'Aragonais',
    },
    local: 'Aragonés',
  },
  {
    value: 'Armenian',

    label: {
      [languageSelectOptions.en.value]: 'Armenian',
      [languageSelectOptions.fr.value]: 'Arménien',
    },
    local: 'Հայերեն',
  },
  {
    value: 'Assamese',

    label: {
      [languageSelectOptions.en.value]: 'Assamese',
      [languageSelectOptions.fr.value]: 'assamais',
    },
    local: 'অসমীয়া',
  },
  {
    value: 'Avaric',

    label: {
      [languageSelectOptions.en.value]: 'Avaric',
      [languageSelectOptions.fr.value]: 'Avar',
    },
    local: 'Авар',
  },
  {
    value: 'Avestan',

    label: {
      [languageSelectOptions.en.value]: 'Avestan',
      [languageSelectOptions.fr.value]: 'Avestique',
    },
    local: 'avesta',
  },
  {
    value: 'Aymara',

    label: {
      [languageSelectOptions.en.value]: 'Aymara',
      [languageSelectOptions.fr.value]: 'Aymara',
    },
    local: 'Aymar',
  },
  {
    value: 'Azerbaijani',

    label: {
      [languageSelectOptions.en.value]: 'Azerbaijani',
      [languageSelectOptions.fr.value]: 'Azerbaïdjanais',
    },
    local: 'Azərbaycanca',
  },
  {
    value: 'Bambara',

    label: {
      [languageSelectOptions.en.value]: 'Bambara',
      [languageSelectOptions.fr.value]: 'Bambara',
    },
    local: 'Bamanankan',
  },
  {
    value: 'Bashkir',

    label: {
      [languageSelectOptions.en.value]: 'Bashkir',
      [languageSelectOptions.fr.value]: 'Bachkir',
    },
    local: 'Башҡортса',
  },
  {
    value: 'Basque',

    label: {
      [languageSelectOptions.en.value]: 'Basque',
      [languageSelectOptions.fr.value]: 'Basque',
    },
    local: 'Euskara',
  },
  {
    value: 'Belarusian',

    label: {
      [languageSelectOptions.en.value]: 'Belarusian',
      [languageSelectOptions.fr.value]: 'Biélorusse',
    },
    local: 'Беларуская',
  },
  {
    value: 'Bengali',

    label: {
      [languageSelectOptions.en.value]: 'Bengali',
      [languageSelectOptions.fr.value]: 'Bengali',
    },
    local: 'বাংলা',
  },
  {
    value: 'Bihari',

    label: {
      [languageSelectOptions.en.value]: 'Bihari',
      [languageSelectOptions.fr.value]: 'Bihari',
    },
    local: 'भोजपुरी',
  },
  {
    value: 'Bislama',
    label: {
      [languageSelectOptions.en.value]: 'Bislama',
      [languageSelectOptions.fr.value]: 'Bichlamar',
    },
    local: 'Bislama',
  },
  {
    value: 'Bosnian',
    label: {
      [languageSelectOptions.en.value]: 'Bosnian',
      [languageSelectOptions.fr.value]: 'Bosniaque',
    },
    local: 'Bosanski',
  },
  {
    value: 'Breton',
    label: {
      [languageSelectOptions.en.value]: 'Breton',
      [languageSelectOptions.fr.value]: 'Breton',
    },
    local: 'Brezhoneg',
  },
  {
    value: 'Bulgarian',

    label: {
      [languageSelectOptions.en.value]: 'Bulgarian',
      [languageSelectOptions.fr.value]: 'Bulgare',
    },
    local: 'Български',
  },
  {
    value: 'Burmese',
    label: {
      [languageSelectOptions.en.value]: 'Burmese',
      [languageSelectOptions.fr.value]: 'Birman',
    },
    local: 'မြန်မာဘာသာ',
  },
  {
    value: 'Catalan',
    label: {
      [languageSelectOptions.en.value]: 'Catalan',
      [languageSelectOptions.fr.value]: 'Catalan',
    },
    local: 'Català',
  },
  {
    value: 'Chamorro',

    label: {
      [languageSelectOptions.en.value]: 'Chamorro',
      [languageSelectOptions.fr.value]: 'Chamorro',
    },
    local: 'Chamoru',
  },
  {
    value: 'Chechen',

    label: {
      [languageSelectOptions.en.value]: 'Chechen',
      [languageSelectOptions.fr.value]: 'Tchétchène',
    },
    local: 'Нохчийн',
  },
  {
    value: 'Chichewa',

    label: {
      [languageSelectOptions.en.value]: 'Chichewa',
      [languageSelectOptions.fr.value]: 'Chichewa',
    },
    local: 'chiCheŵa',
  },
  {
    value: 'Chinese',

    label: {
      [languageSelectOptions.en.value]: 'Chinese',
      [languageSelectOptions.fr.value]: 'Chinois',
    },
    local: '中文',
  },
  {
    value: 'Chuvash',
    label: {
      [languageSelectOptions.en.value]: 'Chuvash',
      [languageSelectOptions.fr.value]: 'Tchouvache',
    },
    local: 'Чӑвашла',
  },
  {
    value: 'Cornish',
    label: {
      [languageSelectOptions.en.value]: 'Cornish',
      [languageSelectOptions.fr.value]: 'Cornique',
    },
    local: 'Kernowek',
  },
  {
    value: 'Corsican',
    label: {
      [languageSelectOptions.en.value]: 'Corsican',
      [languageSelectOptions.fr.value]: 'Corse',
    },
    local: 'Corsu',
  },
  {
    value: 'Cree',
    label: {
      [languageSelectOptions.en.value]: 'Cree',
      [languageSelectOptions.fr.value]: 'Cree',
    },
    local: 'ᓀᐦᐃᔭᐍᐏᐣ',
  },
  {
    value: 'Croatian',

    label: {
      [languageSelectOptions.en.value]: 'Croatian',
      [languageSelectOptions.fr.value]: 'Croate',
    },
    local: 'Hrvatski',
  },
  {
    value: 'Czech',

    label: {
      [languageSelectOptions.en.value]: 'Czech',
      [languageSelectOptions.fr.value]: 'Tchèque',
    },
    local: 'Čeština',
  },
  {
    value: 'Danish',

    label: {
      [languageSelectOptions.en.value]: 'Danish',
      [languageSelectOptions.fr.value]: 'Danois',
    },
    local: 'Dansk',
  },
  {
    value: 'Divehi',

    label: {
      [languageSelectOptions.en.value]: 'Divehi',
      [languageSelectOptions.fr.value]: 'Maldivien',
    },
    local: 'Divehi',
  },
  {
    value: 'Dutch',

    label: {
      [languageSelectOptions.en.value]: 'Dutch',
      [languageSelectOptions.fr.value]: 'Néerlandais',
    },
    local: 'Nederlands',
  },
  {
    value: 'Dzongkha',

    label: {
      [languageSelectOptions.en.value]: 'Dzongkha',
      [languageSelectOptions.fr.value]: 'Dzongkha',
    },
    local: 'རྫོང་ཁ',
  },
  {
    value: 'English',

    label: {
      [languageSelectOptions.en.value]: 'English',
      [languageSelectOptions.fr.value]: 'Anglais',
    },
    local: 'English',
  },
  {
    value: 'Esperanto',
    label: {
      [languageSelectOptions.en.value]: 'Esperanto',
      [languageSelectOptions.fr.value]: 'Espéranto',
    },
    local: 'Esperanto',
  },
  {
    value: 'Estonian',
    label: {
      [languageSelectOptions.en.value]: 'Estonian',
      [languageSelectOptions.fr.value]: 'Estonien',
    },
    local: 'Eesti',
  },
  {
    value: 'Ewe',
    label: {
      [languageSelectOptions.en.value]: 'Ewe',
      [languageSelectOptions.fr.value]: 'Éwé',
    },
    local: 'Ɛʋɛgbɛ',
  },
  {
    value: 'Faroese',
    label: {
      [languageSelectOptions.en.value]: 'Faroese',
      [languageSelectOptions.fr.value]: 'Féroïen',
    },
    local: 'Føroyskt',
  },
  {
    value: 'Fijian',
    label: {
      [languageSelectOptions.en.value]: 'Fijian',
      [languageSelectOptions.fr.value]: 'Fidjien',
    },
    local: 'Vosa Vakaviti',
  },
  {
    value: 'Finnish',

    label: {
      [languageSelectOptions.en.value]: 'Finnish',
      [languageSelectOptions.fr.value]: 'Finlandais',
    },
    local: 'Suomi',
  },
  {
    value: 'French',

    label: {
      [languageSelectOptions.en.value]: 'French',
      [languageSelectOptions.fr.value]: 'Français',
    },
    local: 'Français',
  },
  {
    value: 'Fulah',
    label: {
      [languageSelectOptions.en.value]: 'Fulah',
      [languageSelectOptions.fr.value]: 'Peul',
    },
    local: 'Fulfulde',
  },
  {
    value: 'Galician',
    label: {
      [languageSelectOptions.en.value]: 'Galician',
      [languageSelectOptions.fr.value]: 'Galicien',
    },
    local: 'Galego',
  },
  {
    value: 'Georgian',
    label: {
      [languageSelectOptions.en.value]: 'Georgian',
      [languageSelectOptions.fr.value]: 'Géorgien',
    },
    local: 'ქართული',
  },
  {
    value: 'German',

    label: {
      [languageSelectOptions.en.value]: 'German',
      [languageSelectOptions.fr.value]: 'Allemand',
    },
    local: 'Deutsch',
  },
  {
    value: 'Greek',

    label: {
      [languageSelectOptions.en.value]: 'Greek',
      [languageSelectOptions.fr.value]: 'Grec',
    },
    local: 'Ελληνικά',
  },
  {
    value: 'Guaraní',
    label: {
      [languageSelectOptions.en.value]: 'Guaraní',
      [languageSelectOptions.fr.value]: 'Guaraní',
    },
    local: "Avañe'ẽ",
  },
  {
    value: 'Gujarati',
    label: {
      [languageSelectOptions.en.value]: 'Gujarati',
      [languageSelectOptions.fr.value]: 'Gujarati',
    },
    local: 'ગુજરાતી',
  },
  {
    value: 'Haitian',
    label: {
      [languageSelectOptions.en.value]: 'Haitian',
      [languageSelectOptions.fr.value]: 'Créole haïtien',
    },
    local: 'Kreyòl Ayisyen',
  },
  {
    value: 'Hausa',
    label: {
      [languageSelectOptions.en.value]: 'Hausa',
      [languageSelectOptions.fr.value]: 'Haoussa',
    },
    local: 'Hausa',
  },
  {
    value: 'Hebrew',

    label: {
      [languageSelectOptions.en.value]: 'Hebrew',
      [languageSelectOptions.fr.value]: 'Hébreu',
    },
    local: 'עברית',
  },
  {
    value: 'Herero',
    label: {
      [languageSelectOptions.en.value]: 'Herero',
      [languageSelectOptions.fr.value]: 'Herero',
    },
    local: 'Otjiherero',
  },
  {
    value: 'Hindi',

    label: {
      [languageSelectOptions.en.value]: 'Hindi',
      [languageSelectOptions.fr.value]: 'Hindi',
    },
    local: 'हिन्दी',
  },
  {
    value: 'HiriMotu',

    label: {
      [languageSelectOptions.en.value]: 'Hiri Motu',
      [languageSelectOptions.fr.value]: 'Hiri motu',
    },
    local: 'Hiri Motu',
  },
  {
    value: 'Hungarian',

    label: {
      [languageSelectOptions.en.value]: 'Hungarian',
      [languageSelectOptions.fr.value]: 'Hongrois',
    },
    local: 'Magyar',
  },
  {
    value: 'Interlingua',
    label: {
      [languageSelectOptions.en.value]: 'Interlingua',
      [languageSelectOptions.fr.value]: 'Interlingua',
    },
    local: 'Interlingua',
  },
  {
    value: 'Indonesian',

    label: {
      [languageSelectOptions.en.value]: 'Indonesian',
      [languageSelectOptions.fr.value]: 'Indonésien',
    },
    local: 'Bahasa Indonesia',
  },
  {
    value: 'Interlingue',
    label: {
      [languageSelectOptions.en.value]: 'Interlingue',
      [languageSelectOptions.fr.value]: 'interlingue',
    },
    local: 'Interlingue',
  },
  {
    value: 'Irish',

    label: {
      [languageSelectOptions.en.value]: 'Irish',
      [languageSelectOptions.fr.value]: 'Irlandais',
    },
    local: 'Gaeilge',
  },
  {
    value: 'Igbo',
    label: {
      [languageSelectOptions.en.value]: 'Igbo',
      [languageSelectOptions.fr.value]: 'Igbo',
    },
    local: 'Igbo',
  },
  {
    value: 'Inupiaq',
    label: {
      [languageSelectOptions.en.value]: 'Inupiaq',
      [languageSelectOptions.fr.value]: 'Inupiaq',
    },
    local: 'Iñupiak',
  },
  {
    value: 'Ido',
    label: {
      [languageSelectOptions.en.value]: 'Ido',
      [languageSelectOptions.fr.value]: 'Ido',
    },
    local: 'Ido',
  },
  {
    value: 'Icelandic',

    label: {
      [languageSelectOptions.en.value]: 'Icelandic',
      [languageSelectOptions.fr.value]: 'Islandais',
    },
    local: 'Íslenska',
  },
  {
    value: 'Italian',

    label: {
      [languageSelectOptions.en.value]: 'Italian',
      [languageSelectOptions.fr.value]: 'Italien',
    },
    local: 'Italiano',
  },
  {
    value: 'Inuktitut',
    label: {
      [languageSelectOptions.en.value]: 'Inuktitut',
      [languageSelectOptions.fr.value]: 'Inuktitut',
    },
    local: 'ᐃᓄᒃᑎᑐᑦ',
  },
  {
    value: 'Japanese',

    label: {
      [languageSelectOptions.en.value]: 'Japanese',
      [languageSelectOptions.fr.value]: 'Japonais',
    },
    local: '日本語',
  },
  {
    value: 'Javanese',

    label: {
      [languageSelectOptions.en.value]: 'Javanese',
      [languageSelectOptions.fr.value]: 'Javanais',
    },
    local: 'Basa Jawa',
  },
  {
    value: 'Kalaallisut',

    label: {
      [languageSelectOptions.en.value]: 'Kalaallisut',
      [languageSelectOptions.fr.value]: 'Groenlandais',
    },
    local: 'Kalaallisut',
  },
  {
    value: 'Kannada',

    label: {
      [languageSelectOptions.en.value]: 'Kannada',
      [languageSelectOptions.fr.value]: 'Kannada',
    },
    local: 'ಕನ್ನಡ',
  },
  {
    value: 'Kanuri',

    label: {
      [languageSelectOptions.en.value]: 'Kanuri',
      [languageSelectOptions.fr.value]: 'Kanouri',
    },
    local: 'Kanuri',
  },
  {
    value: 'Kashmiri',

    label: {
      [languageSelectOptions.en.value]: 'Kashmiri',
      [languageSelectOptions.fr.value]: 'Kashmiri',
    },
    local: 'كشميري',
  },
  {
    value: 'Kazakh',

    label: {
      [languageSelectOptions.en.value]: 'Kazakh',
      [languageSelectOptions.fr.value]: 'Kazakh',
    },
    local: 'Қазақша',
  },
  {
    value: 'Khmer',
    label: {
      [languageSelectOptions.en.value]: 'Khmer',
      [languageSelectOptions.fr.value]: 'Khmer',
    },
    local: 'ភាសាខ្មែរ',
  },
  {
    value: 'Kikuyu',

    label: {
      [languageSelectOptions.en.value]: 'Kikuyu',
      [languageSelectOptions.fr.value]: 'Kikuyu',
    },
    local: 'Gĩkũyũ',
  },
  {
    value: 'Kinyarwanda',

    label: {
      [languageSelectOptions.en.value]: 'Kinyarwanda',
      [languageSelectOptions.fr.value]: 'Kinyarwanda',
    },
    local: 'Kinyarwanda',
  },
  {
    value: 'Kyrgyz',

    label: {
      [languageSelectOptions.en.value]: 'Kyrgyz',
      [languageSelectOptions.fr.value]: 'Kirghize',
    },
    local: 'Кыргызча',
  },
  {
    value: 'Komi',

    label: {
      [languageSelectOptions.en.value]: 'Komi',
      [languageSelectOptions.fr.value]: 'Komi',
    },
    local: 'Коми',
  },
  {
    value: 'Kongo',

    label: {
      [languageSelectOptions.en.value]: 'Kongo',
      [languageSelectOptions.fr.value]: 'Kikongo',
    },
    local: 'Kongo',
  },
  {
    value: 'Korean',

    label: {
      [languageSelectOptions.en.value]: 'Korean',
      [languageSelectOptions.fr.value]: 'Coréen',
    },
    local: '한국어',
  },
  {
    value: 'Kurdish',
    label: {
      [languageSelectOptions.en.value]: 'Kurdish',
      [languageSelectOptions.fr.value]: 'Kurde',
    },
    local: 'Kurdî',
  },
  {
    value: 'Kwanyama',
    label: {
      [languageSelectOptions.en.value]: 'Kwanyama',
      [languageSelectOptions.fr.value]: 'Kuanyama',
    },
    local: 'Kwanyama',
  },
  {
    value: 'Latin',

    label: {
      [languageSelectOptions.en.value]: 'Latin',
      [languageSelectOptions.fr.value]: 'Latin',
    },
    local: 'Latina',
  },
  {
    value: 'Luxembourgish',

    label: {
      [languageSelectOptions.en.value]: 'Luxembourgish',
      [languageSelectOptions.fr.value]: 'Luxembourgeois',
    },
    local: 'Lëtzebuergesch',
  },
  {
    value: 'Ganda',
    label: {
      [languageSelectOptions.en.value]: 'Ganda',
      [languageSelectOptions.fr.value]: 'Ganda',
    },
    local: 'Luganda',
  },
  {
    value: 'Limburgish',

    label: {
      [languageSelectOptions.en.value]: 'Limburgish',
      [languageSelectOptions.fr.value]: 'Limbourgeois',
    },
    local: 'Limburgs',
  },
  {
    label: {
      [languageSelectOptions.en.value]: 'Lingala',
      [languageSelectOptions.fr.value]: 'Lingala',
    },
    local: 'Lingála',
  },
  {
    value: 'Lao',

    label: {
      [languageSelectOptions.en.value]: 'Lao',
      [languageSelectOptions.fr.value]: 'Lao',
    },
    local: 'ພາສາລາວ',
  },
  {
    value: 'Lithuanian',

    label: {
      [languageSelectOptions.en.value]: 'Lithuanian',
      [languageSelectOptions.fr.value]: 'Lituanien',
    },
    local: 'Lietuvių',
  },
  {
    value: 'LubaKatanga',

    label: {
      [languageSelectOptions.en.value]: 'Luba-Katanga',
      [languageSelectOptions.fr.value]: 'Luba-katanga',
    },
    local: 'Tshiluba',
  },
  {
    value: 'Latvian',

    label: {
      [languageSelectOptions.en.value]: 'Latvian',
      [languageSelectOptions.fr.value]: 'Letton',
    },
    local: 'Latviešu',
  },
  {
    value: 'Manx',

    label: {
      [languageSelectOptions.en.value]: 'Manx',
      [languageSelectOptions.fr.value]: 'Manx',
    },
    local: 'Gaelg',
  },
  {
    value: 'Macedonian',

    label: {
      [languageSelectOptions.en.value]: 'Macedonian',
      [languageSelectOptions.fr.value]: 'Macédonien',
    },
    local: 'Македонски',
  },
  {
    value: 'Malagasy',

    label: {
      [languageSelectOptions.en.value]: 'Malagasy',
      [languageSelectOptions.fr.value]: 'Malgache',
    },
    local: 'Malagasy',
  },
  {
    value: 'Malay',

    label: {
      [languageSelectOptions.en.value]: 'Malay',
      [languageSelectOptions.fr.value]: 'Malais',
    },
    local: 'Bahasa Melayu',
  },
  {
    value: 'Malayalam',

    label: {
      [languageSelectOptions.en.value]: 'Malayalam',
      [languageSelectOptions.fr.value]: 'Malayalam',
    },
    local: 'മലയാളം',
  },
  {
    value: 'Maltese',

    label: {
      [languageSelectOptions.en.value]: 'Maltese',
      [languageSelectOptions.fr.value]: 'Maltais',
    },
    local: 'Malti',
  },
  {
    value: 'Māori',

    label: {
      [languageSelectOptions.en.value]: 'Maori',
      [languageSelectOptions.fr.value]: 'Maori',
    },
    local: 'Māori',
  },
  {
    value: 'Marathi',

    label: {
      [languageSelectOptions.en.value]: 'Marathi',
      [languageSelectOptions.fr.value]: 'Marathe',
    },
    local: 'मराठी',
  },
  {
    value: 'Marshallese',

    label: {
      [languageSelectOptions.en.value]: 'Marshallese',
      [languageSelectOptions.fr.value]: 'Marshallais',
    },
    local: 'Kajin M̧ajeļ',
  },
  {
    value: 'Mongolian',

    label: {
      [languageSelectOptions.en.value]: 'Mongolian',
      [languageSelectOptions.fr.value]: 'Mongol',
    },
    local: 'Монгол',
  },
  {
    value: 'Nauru',

    label: {
      [languageSelectOptions.en.value]: 'Nauru',
      [languageSelectOptions.fr.value]: 'Nauruan',
    },
    local: 'Dorerin Naoero',
  },
  {
    value: 'Navajo',

    label: {
      [languageSelectOptions.en.value]: 'Navajo',
      [languageSelectOptions.fr.value]: 'Navajo',
    },
    local: 'Diné Bizaad',
  },
  {
    IDBCursorWithValue: 'Northern Ndebele',

    label: {
      [languageSelectOptions.en.value]: 'Northern Ndebele',
      [languageSelectOptions.fr.value]: 'Ndébélé du Nord',
    },
    local: 'isiNdebele',
  },
  {
    value: 'Nepali',

    label: {
      [languageSelectOptions.en.value]: 'Nepali',
      [languageSelectOptions.fr.value]: 'Népalais',
    },
    local: 'नेपाली',
  },
  {
    value: 'Ndonga',

    label: {
      [languageSelectOptions.en.value]: 'Ndonga',
      [languageSelectOptions.fr.value]: 'Ndonga',
    },
    local: 'Owambo',
  },

  {
    value: 'Norwegian',

    label: {
      [languageSelectOptions.en.value]: 'Norwegian',
      [languageSelectOptions.fr.value]: 'Norvégien',
    },
    local: 'Norsk',
  },
  {
    value: 'Nuosu',

    label: {
      [languageSelectOptions.en.value]: 'Sichuan Yi, Nuosu',
      [languageSelectOptions.fr.value]: 'Yi de Sichuan',
    },
    local: 'ꆈꌠ꒿ Nuosuhxop',
  },
  {
    value: 'SouthernNdebele',
    label: {
      [languageSelectOptions.en.value]: 'Southern Ndebele',
      [languageSelectOptions.fr.value]: 'Ndebele du Sud',
    },
    local: 'isiNdebele',
  },
  {
    value: 'Occitan',
    label: {
      [languageSelectOptions.en.value]: 'Occitan',
      [languageSelectOptions.fr.value]: 'Occitan',
    },
    local: 'Occitan',
  },
  {
    value: 'Ojibwe',
    label: {
      [languageSelectOptions.en.value]: 'Ojibwe',
      [languageSelectOptions.fr.value]: 'Ojibwé',
    },
    local: 'ᐊᓂᔑᓈᐯᒧᐎᓐ',
  },
  {
    value: 'OldChurchSlavonic',
    label: {
      [languageSelectOptions.en.value]: 'Old Church Slavonic',
      [languageSelectOptions.fr.value]: 'Vieux slave ecclésiastique',
    },
    local: 'Словѣ́ньскъ',
  },
  {
    value: 'Oromo',

    label: {
      [languageSelectOptions.en.value]: 'Oromo',
      [languageSelectOptions.fr.value]: 'Oromo',
    },
    local: 'Afaan Oromoo',
  },
  {
    value: 'Oriya',

    label: {
      [languageSelectOptions.en.value]: 'Oriya',
      [languageSelectOptions.fr.value]: 'Oriya',
    },
    local: 'ଓଡି଼ଆ',
  },
  {
    value: 'Ossetian',

    label: {
      [languageSelectOptions.en.value]: 'Ossetian',
      [languageSelectOptions.fr.value]: 'Ossète',
    },
    local: 'Ирон æвзаг',
  },
  {
    value: 'Punjabi',

    label: {
      [languageSelectOptions.en.value]: 'Punjabi',
      [languageSelectOptions.fr.value]: 'Pendjabi',
    },
    local: 'ਪੰਜਾਬੀ',
  },
  {
    value: 'Pāli',

    label: {
      [languageSelectOptions.en.value]: 'Pali',
      [languageSelectOptions.fr.value]: 'Pali',
    },
    local: 'पाऴि',
  },
  {
    value: 'Persian',

    label: {
      [languageSelectOptions.en.value]: 'Persian',
      [languageSelectOptions.fr.value]: 'Persan',
    },
    local: 'فارسی',
  },
  {
    value: 'Polish',

    label: {
      [languageSelectOptions.en.value]: 'Polish',
      [languageSelectOptions.fr.value]: 'Polonais',
    },
    local: 'Polski',
  },
  {
    value: 'Pashto',

    label: {
      [languageSelectOptions.en.value]: 'Pashto',
      [languageSelectOptions.fr.value]: 'Pashto',
    },
    local: 'پښتو',
  },
  {
    value: 'Portuguese',

    label: {
      [languageSelectOptions.en.value]: 'Portuguese',
      [languageSelectOptions.fr.value]: 'Portugais',
    },
    local: 'Português',
  },
  {
    value: 'Quechua',
    label: {
      [languageSelectOptions.en.value]: 'Quechua',
      [languageSelectOptions.fr.value]: 'Quechua',
    },
    local: 'Runa Simi',
  },
  {
    value: 'Romansh',

    label: {
      [languageSelectOptions.en.value]: 'Romansh',
      [languageSelectOptions.fr.value]: 'Romanche',
    },
    local: 'Rumantsch',
  },
  {
    value: 'Kirundi',
    label: {
      [languageSelectOptions.en.value]: 'Kirundi',
      [languageSelectOptions.fr.value]: 'kirundi',
    },
    local: 'Kirundi',
  },
  {
    value: 'Romanian',
    label: {
      [languageSelectOptions.en.value]: 'Romanian',
      [languageSelectOptions.fr.value]: 'Roumain',
    },
    local: 'Română',
  },
  {
    value: 'Russian',
    label: {
      [languageSelectOptions.en.value]: 'Russian',
      [languageSelectOptions.fr.value]: 'Russe',
    },
    local: 'Русский',
  },
  {
    value: 'Sanskrit',
    label: {
      [languageSelectOptions.en.value]: 'Sanskrit',
      [languageSelectOptions.fr.value]: 'Sanskrit',
    },
    local: 'संस्कृतम्',
  },
  {
    value: 'Sardinian',
    label: {
      [languageSelectOptions.en.value]: 'Sardinian',
      [languageSelectOptions.fr.value]: 'Sarde',
    },
    local: 'Sardu',
  },
  {
    value: 'Sindhi',
    label: {
      [languageSelectOptions.en.value]: 'Sindhi',
      [languageSelectOptions.fr.value]: 'Sindhi',
    },
    local: 'سنڌي',
  },
  {
    value: 'NorthenSami',
    label: {
      [languageSelectOptions.en.value]: 'Northern Sami',
      [languageSelectOptions.fr.value]: 'Same du Nord',
    },
    local: 'Sámegiella',
  },
  {
    value: 'Samoan',
    label: {
      [languageSelectOptions.en.value]: 'Samoan',
      [languageSelectOptions.fr.value]: 'Samoan',
    },
    local: 'Gagana Sāmoa',
  },
  {
    value: 'Sango',
    label: {
      [languageSelectOptions.en.value]: 'Sango',
      [languageSelectOptions.fr.value]: 'Sango',
    },
    local: 'Sängö',
  },
  {
    value: 'Serbian',
    label: {
      [languageSelectOptions.en.value]: 'Serbian',
      [languageSelectOptions.fr.value]: 'Serbe',
    },
    local: 'Српски',
  },
  {
    value: 'Gaelic',
    label: {
      [languageSelectOptions.en.value]: 'Gaelic',
      [languageSelectOptions.fr.value]: 'Gaélique écossais',
    },
    local: 'Gàidhlig',
  },
  {
    value: 'Shona',
    label: {
      [languageSelectOptions.en.value]: 'Shona',
      [languageSelectOptions.fr.value]: 'Shona',
    },
    local: 'ChiShona',
  },
  {
    value: 'Sinhala',
    label: {
      [languageSelectOptions.en.value]: 'Sinhalese',
      [languageSelectOptions.fr.value]: 'Cingalais',
    },
    local: 'සිංහල',
  },
  {
    value: 'Slovak',
    label: {
      [languageSelectOptions.en.value]: 'Slovak',
      [languageSelectOptions.fr.value]: 'Slovaque',
    },
    local: 'Slovenčina',
  },
  {
    value: 'Slovene',
    label: {
      [languageSelectOptions.en.value]: 'Slovenian',
      [languageSelectOptions.fr.value]: 'Slovène',
    },
    local: 'Slovenščina',
  },
  {
    value: 'Somali',
    label: {
      [languageSelectOptions.en.value]: 'Somali',
      [languageSelectOptions.fr.value]: 'Somali',
    },
    local: 'Soomaaliga',
  },
  {
    value: 'SouthernSotho',
    label: {
      [languageSelectOptions.en.value]: 'Southern Sotho',
      [languageSelectOptions.fr.value]: 'Sotho du Sud',
    },
    local: 'Sesotho',
  },
  {
    value: 'Spanish',
    label: {
      [languageSelectOptions.en.value]: 'Spanish',
      [languageSelectOptions.fr.value]: 'Espagnol',
    },
    local: 'Español',
  },
  {
    value: 'Sundanese',
    label: {
      [languageSelectOptions.en.value]: 'Sundanese',
      [languageSelectOptions.fr.value]: 'Soundanais',
    },
    local: 'Basa Sunda',
  },
  {
    value: 'Swahili',
    label: {
      [languageSelectOptions.en.value]: 'Swahili',
      [languageSelectOptions.fr.value]: 'Souahéli',
    },
    local: 'Kiswahili',
  },
  {
    value: 'Swati',
    label: {
      [languageSelectOptions.en.value]: 'Swati',
      [languageSelectOptions.fr.value]: 'Swati',
    },
    local: 'SiSwati',
  },
  {
    value: 'Swedish',
    label: {
      [languageSelectOptions.en.value]: 'Swedish',
      [languageSelectOptions.fr.value]: 'Suédois',
    },
    local: 'Svenska',
  },
  {
    value: 'Tamil',
    label: {
      [languageSelectOptions.en.value]: 'Tamil',
      [languageSelectOptions.fr.value]: 'Tamoul',
    },
    local: 'தமிழ்',
  },
  {
    value: 'Telegu',
    label: {
      [languageSelectOptions.en.value]: 'Telugu',
      [languageSelectOptions.fr.value]: 'Télougou',
    },
    local: 'తెలుగు',
  },
  {
    value: 'Tajik',

    label: {
      [languageSelectOptions.en.value]: 'Tajik',
      [languageSelectOptions.fr.value]: 'Tadjik',
    },
    local: 'Тоҷикӣ',
  },
  {
    value: 'Thai',

    label: {
      [languageSelectOptions.en.value]: 'Thai',
      [languageSelectOptions.fr.value]: 'Thaï',
    },
    local: 'ภาษาไทย',
  },
  {
    value: 'Tigrinya',

    label: {
      [languageSelectOptions.en.value]: 'Tigrinya',
      [languageSelectOptions.fr.value]: 'Tigrigna',
    },
    local: 'ትግርኛ',
  },
  {
    value: 'Tibetan',

    label: {
      [languageSelectOptions.en.value]: 'Tibetan',
      [languageSelectOptions.fr.value]: 'Tibétain',
    },
    local: 'བོད་ཡིག',
  },
  {
    value: 'Turkmen',
    label: {
      [languageSelectOptions.en.value]: 'Turkmen',
      [languageSelectOptions.fr.value]: 'Turkmène',
    },
    local: 'Türkmençe',
  },
  {
    value: 'Tagalog',

    label: {
      [languageSelectOptions.en.value]: 'Tagalog',
      [languageSelectOptions.fr.value]: 'Tagal',
    },
    local: 'Tagalog',
  },
  {
    value: 'Tswana',

    label: {
      [languageSelectOptions.en.value]: 'Tswana',
      [languageSelectOptions.fr.value]: 'Tswana',
    },
    local: 'Setswana',
  },
  {
    value: 'Tonga',

    label: {
      [languageSelectOptions.en.value]: 'Tonga',
      [languageSelectOptions.fr.value]: 'Tongan',
    },
    local: 'faka Tonga',
  },
  {
    value: 'Turkish',

    label: {
      [languageSelectOptions.en.value]: 'Turkish',
      [languageSelectOptions.fr.value]: 'Turc',
    },
    local: 'Türkçe',
  },
  {
    value: 'Tsonga',
    label: {
      [languageSelectOptions.en.value]: 'Tsonga',
      [languageSelectOptions.fr.value]: 'Tsonga',
    },
    local: 'Xitsonga',
  },
  {
    value: 'Tatra',

    label: {
      [languageSelectOptions.en.value]: 'Tatar',
      [languageSelectOptions.fr.value]: 'Tatar',
    },
    local: 'Татарча',
  },
  {
    value: 'Twi',
    label: {
      [languageSelectOptions.en.value]: 'Twi',
      [languageSelectOptions.fr.value]: 'Twi',
    },
    local: 'Twi',
  },
  {
    value: 'Tahitian',
    label: {
      [languageSelectOptions.en.value]: 'Tahitian',
      [languageSelectOptions.fr.value]: 'Tahitien',
    },
    local: 'Reo Mā’ohi',
  },
  {
    value: 'Uyghur',

    label: {
      [languageSelectOptions.en.value]: 'Uyghur',
      [languageSelectOptions.fr.value]: 'Ouïgour',
    },
    local: 'ئۇيغۇرچه',
  },
  {
    value: 'Ukrainian',

    label: {
      [languageSelectOptions.en.value]: 'Ukrainian',
      [languageSelectOptions.fr.value]: 'Ukrainien',
    },
    local: 'Українська',
  },
  {
    value: 'Urdu',

    label: {
      [languageSelectOptions.en.value]: 'Urdu',
      [languageSelectOptions.fr.value]: 'Ourdou',
    },
    local: 'اردو',
  },
  {
    value: 'Uzbek',
    label: {
      [languageSelectOptions.en.value]: 'Uzbek',
      [languageSelectOptions.fr.value]: 'Ouzbek',
    },
    local: 'O‘zbek',
  },
  {
    value: 'Venda',

    label: {
      [languageSelectOptions.en.value]: 'Venda',
      [languageSelectOptions.fr.value]: 'Venda',
    },
    local: 'Tshivenḓa',
  },
  {
    value: 'Vietlabelse',

    label: 'Vietlabelse',
    local: 'Tiếng Việt',
  },
  {
    value: 'Volapük',

    label: {
      [languageSelectOptions.en.value]: 'Volapük',
      [languageSelectOptions.fr.value]: 'Volapük',
    },
    local: 'Volapük',
  },
  {
    value: 'Walloon',
    label: {
      [languageSelectOptions.en.value]: 'Walloon',
      [languageSelectOptions.fr.value]: 'Wallon',
    },
    local: 'Walon',
  },
  {
    value: 'Welsh',

    label: {
      [languageSelectOptions.en.value]: 'Welsh',
      [languageSelectOptions.fr.value]: 'Gallois',
    },
    local: 'Cymraeg',
  },
  {
    value: 'Wolof',
    label: {
      [languageSelectOptions.en.value]: 'Wolof',
      [languageSelectOptions.fr.value]: 'Wolof',
    },
    local: 'Wolof',
  },
  {
    value: 'WesternFrisian',
    label: {
      [languageSelectOptions.en.value]: 'Western Frisian',
      [languageSelectOptions.fr.value]: 'Frison occidental',
    },
    local: 'Frysk',
  },
  {
    value: 'Xhosa',

    label: {
      [languageSelectOptions.en.value]: 'Xhosa',
      [languageSelectOptions.fr.value]: 'Xhosa',
    },
    local: 'isiXhosa',
  },
  {
    value: 'Yiddish',

    label: {
      [languageSelectOptions.en.value]: 'Yiddish',
      [languageSelectOptions.fr.value]: 'Yiddish',
    },
    local: 'ייִדיש',
  },
  {
    value: 'Yoruba',
    label: {
      [languageSelectOptions.en.value]: 'Yoruba',
      [languageSelectOptions.fr.value]: 'Yoruba',
    },
    local: 'Yorùbá',
  },
  {
    value: 'Zhuang',
    label: {
      [languageSelectOptions.en.value]: 'Zhuang',
      [languageSelectOptions.fr.value]: 'Zhuang',
    },
    local: 'Saɯ cueŋƅ, Saw cuengh',
  },
  {
    value: 'Zulu',
    label: {
      [languageSelectOptions.en.value]: 'Zulu',
      [languageSelectOptions.fr.value]: 'Zoulou',
    },
    local: 'isiZulu',
  },
];
export const blockedType = {
  inDefinite: 'inDefinite',
  definite: 'definite',
};

export const listingScheduleType = {
  oneTime: 'oneTime',
  recurring: 'recurring',
};

export const earningFetchType = {
  lastWeek: 'lastWeek',
  lastMonth: 'lastMonth',
  yearToDate: 'yearToDate',
  totalEarning: 'totalEarning',
};

export const gstNumber = '818250920 RT0001';
export const qstNumber = '1222752694 TQ0001';
export const djobMail = 'comptabilite@djob.co';
export const djobSite = 'djob.co';
export const workerDashboardTab = {
  earning: 'earning',
  pastShifts: 'pastShifts',
  upcomingShifts: 'upcomingShifts',
  currentShifts: 'currentShifts',
};

export const MIN_START_HOUR_DIFF = 12; //12 hours
export const MIN_WORKING_HOUR = 3; // 3 hours
export const AVERAGE_WORKING_HOUR = 8;
export const MAX_WORKING_HOUR = 12; //
export const MAX_START_HOUR = 23; //23 - MIN_WROKING_HOUR;
export const MIN_WORKER_START_HOUR = parseInt(process.env.REACT_APP_MIN_WORKER_START_HOUR || '1'); // 6 default worker minmum start hours :6
